<template>
  <div class="max-w-full mx-auto px-6 py-6 dark:bg-gray-600 dark:border-gray-600">
    <InternalBroadcastBanner />
    <div class="flex justify-between items-center mb-4">
      <div class="flex space-x-4">
        <select v-model="filters.status" @change="fetchTickets"
          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
          <option value="All">All Tickets</option>
          <option value="Open">Open</option>
          <option value="Closed">Closed</option>
          <!-- <option value="Today">Today</option> -->
        </select>

        <!-- Clients with Open Tickets Filter (internal only — irrelevant to external techs) -->
        <select v-if="!userroles.includes(80)" v-model="filters.client_id" @change="setClientFilter($event.target.value)"
          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
          <option value="All" selected disabled>My Clients with Open Tickets</option>
          <option v-for="client in clientsWithOpenTickets" :key="client.id" :value="client.id">
            {{ client.companyname }}
          </option>
        </select>
        <button @click="setTodayFilters" class="px-4 mt-1 font-bold text-sm rounded-md bg-gradient-to-b from-gray-300 to-gray-400 text-gray-800 border border-gray-400 shadow-sm
                  dark:from-gray-700 dark:to-gray-800 dark:text-gray-200 dark:border-gray-600 hover:from-gray-400 hover:to-gray-500
                  dark:hover:from-gray-600 dark:hover:to-gray-700 transition">
          Today
        </button>
      </div>
      <div class="flex items-center justify-left space-x-2" @click="toggleFilters">
        <button class="bg-yellow-500 text-white px-4 py-2 rounded-md flex items-center font-medium hover:bg-yellow-400">
          <svg class="h-6 w-6 text-white" width="24" height="24" viewBox="0 0 24 24" stroke-width="2"
            stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" />
            <path d="M5.5 5h13a1 1 0 0 1 0.5 1.5L14 12L14 19L10 16L10 12L5 6.5a1 1 0 0 1 0.5 -1.5" />
          </svg>
          Filter
        </button>
        <button class="focus:outline-none transition-transform duration-200">
          <svg :class="{ 'rotate-180': showFilters }"
            class="w-4 h-4 text-gray-600 dark:text-white transition-transform duration-200" aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 10">
            <path
              d="M15.434 1.235A2 2 0 0 0 13.586 0H2.414A2 2 0 0 0 1 3.414L6.586 9a2 2 0 0 0 2.828 0L15 3.414a2 2 0 0 0 .434-2.179Z" />
          </svg>
        </button>
      </div>
    </div>
    <FilterSidebar :visible="showFilters" :userroles="userroles" @close="showFilters = false" @apply="applyFilters"
      @saveview="saveView" @clear="clearFilters">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div v-click-outside="closeClientDropdown" class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Client</label>
          <div class="relative">
            <input type="text"
              :class="['mt-1 block w-full rounded-md border-gray-300 shadow-sm', invalidInputs.client ? 'border-red-500' : '', 'dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100']"
              placeholder="Client Name" v-model="companyName" @focus="focusClient"
              @keydown.down.prevent="onKeyDown('client', 'down')" @keydown.up.prevent="onKeyDown('client', 'up')"
              @keydown.enter.prevent="onEnter('client', setClient, null)" @blur="emptyClient" />
            <button v-if="companyName && companyName !== 'All'" @click="clearClient"
              class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500">
              <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
                <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </button>
          </div>
          <ul v-if="dropdownState.client.visible" data-field="client"
            class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-100 dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 scrollable-requester-list">
            <li v-for="(client, index) in filteredClients" :key="client.id" @click="setClient(client)" :class="[
              'cursor-pointer p-3',
              dropdownState.client.index === index ? 'bg-blue-100 dark:bg-gray-700 active-item' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
            ]">
              {{ client.companyname }}
            </li>
          </ul>
        </div>
        <div v-click-outside="closeReqDropdown">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Requester</label>
          <div class="relative">
            <input type="text"
              :class="['mt-1 block w-full rounded-md border-gray-300 shadow-sm', invalidInputs.requester ? 'border-red-500' : '', 'dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100']"
              placeholder="Requester Name" v-model="filters.requester" @focus="focusReq" @blur="emptyReq"
              @keydown.down.prevent="onKeyDown('requester', 'down')" @keydown.up.prevent="onKeyDown('requester', 'up')"
              @keydown.enter.prevent="onEnter('requester', setReq, null)" />
            <button v-if="filters.requester && filters.requester !== 'All'" @click="clearReq"
              class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500">
              <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
                <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </button>
          </div>
          <ul v-if="dropdownState.requester.visible" data-field="requester"
            class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-100 dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 scrollable-requester-list">
            <div v-for="(req, index) in filteredRequesters" :key="req.id">
              <li @click="setReq(req)" v-if="req.name && req.name != ' '" :class="[
                'cursor-pointer p-3',
                dropdownState.requester.index === index ? 'bg-blue-100 dark:bg-gray-700 active-item' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
              ]">
                {{ req.name }}<span v-if="req.email"> - {{ req.email }}</span>
              </li>
            </div>
          </ul>
        </div>
        <div v-click-outside="closeTypeDropdown">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Type</label>
          <div class="relative">
            <input type="text"
              :class="['mt-1 block w-full rounded-md border-gray-300 shadow-sm', invalidInputs.type ? 'border-red-500' : '', 'dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100']"
              placeholder="Type" v-model="filters.type" @focus="focusReqType" @blur="emptyType"
              @keydown.down.prevent="onKeyDown('type', 'down')" @keydown.up.prevent="onKeyDown('type', 'up')"
              @keydown.enter.prevent="onEnter('type', setReqType, null)" />
            <button v-if="filters.type && filters.type !== 'All'" @click="clearReqType"
              class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500">
              <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
                <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </button>
          </div>
          <ul v-if="dropdownState.type.visible" data-field="type"
            class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-100 dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 scrollable-requester-list">
            <li v-for="(reqtype, index) in filteredReqTypes" :key="reqtype.id" @click="setReqType(reqtype)" :class="[
              'cursor-pointer p-3',
              dropdownState.type.index === index ? 'bg-blue-100 dark:bg-gray-700 active-item' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
            ]">
              {{ reqtype.description }}
            </li>
          </ul>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Category</label>
          <select v-model="filters.category_id"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
            <option value="All">All</option>
            <option v-for="category in categories" :key="category.id" :value="category.id">{{ category.description }}
            </option>
          </select>
        </div>
        <div v-if="!userroles.includes(80)">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Sprint</label>
          <select v-model="filters.sprint_id"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
            <option value="All">All</option>
            <option value="0">0 (Backlog)</option>
            <option v-for="sprint in sprints" :key="sprint.id" :value="sprint.id">{{ sprint.name }} ({{ sprint.startdate
            }}
              - {{ sprint.enddate }})</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Aging</label>
          <select v-model="filters.aging"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
            <option value="All">All</option>
            <option value="new">New</option>
            <option value="1_2w">1-2 Weeks</option>
            <option value="2_4w">2-4 Weeks</option>
            <option value="1_3m">1-3 Months</option>
            <option value="3m">3+ Months</option>
          </select>
        </div>
        <div v-if="!userroles.some(r => [60, 70, 80].includes(r))">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Billing Type</label>
          <select v-model="filters.billingType"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
            <option value="All">All</option>
            <option v-for="billingType in billingTypes" :key="billingType" :value="billingType">{{ billingType }}
            </option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Tech</label>
          <select v-model="filters.tech_id"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
            <option value="All">All</option>
            <template v-for="(techList, deptName) in groupedTechnicians" :key="deptName">
              <optgroup v-if="deptName" :label="deptName">
                <option v-for="tech in techList" :key="tech.id" :value="tech.id">
                  {{ tech.name }}
                </option>
              </optgroup>
              <template v-else>
                <option v-for="tech in techList" :key="tech.id" :value="tech.id">
                  {{ tech.name }}
                </option>
              </template>
            </template>
            <!-- Inactive techs, grouped the same way and labelled so they're
                 clearly separated from active techs. -->
            <template v-for="(techList, deptName) in groupedInactiveTechnicians" :key="`inactive-${deptName}`">
              <optgroup :label="(deptName ? deptName : 'Inactive') + ' (Inactive)'">
                <option v-for="tech in techList" :key="tech.id" :value="tech.id">
                  {{ tech.name }}
                </option>
              </optgroup>
            </template>
          </select>
        </div>
        <div v-if="!userroles.some(r => [60, 70, 80].includes(r))">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Department</label>
          <select v-model="filters.department"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
            <option value="All">All</option>
            <option v-for="department in departments" :key="department.id" :value="department.id">{{ department.name }}
            </option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Start Date</label>
          <input type="date" v-model="filters.startDate"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">End Date</label>
          <input type="date" v-model="filters.endDate"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Date Type</label>
          <div class="flex space-x-4">
            <label class="flex items-center">
              <input type="radio" value="submitted" v-model="filters.date_type" class="form-radio text-blue-600" />
              <span class="ml-2 text-gray-700 dark:text-gray-100">Submitted</span>
            </label>
            <label class="flex items-center">
              <input type="radio" value="last_activity" v-model="filters.date_type" class="form-radio text-blue-600" />
              <span class="ml-2 text-gray-700 dark:text-gray-100">Last Activity</span>
            </label>
            <label class="flex items-center">
              <input type="radio" value="completed" v-model="filters.date_type" class="form-radio text-blue-600" />
              <span class="ml-2 text-gray-700 dark:text-gray-100">Completed</span>
            </label>
            <label class="flex items-center">
              <input type="radio" value="promised" v-model="filters.date_type" class="form-radio text-blue-600" />
              <span class="ml-2 text-gray-700 dark:text-gray-100">Priority/Promised</span>
            </label>
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div></div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Past Due Only</label>
            <input type="checkbox" v-model="filters.pastdueonly"
              class="mt-1 block rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
          </div>
        </div>
      </div>
    </FilterSidebar>
    <!-- Applied Filters Bubbles -->
    <div v-if="appliedFilterBubbles.length" class="flex flex-wrap gap-2 mb-4">
      <div class="text-gray-800 dark:text-gray-200 mb-2 font-semibold">Filters Applied:</div>
      <span v-for="(filter, index) in appliedFilterBubbles" :key="index"
        class="bg-yellow-500 text-white text-sm font-medium px-3 py-1 rounded-full flex items-center">
        {{ filter.label }}
        <button @click="clearSpecificFilter(filter.key)" class="ml-2 text-white hover:text-red-200">
          &times;
        </button>
      </span>
    </div>
    <!-- If No Filters Applied -->
    <div v-else class="text-gray-500 dark:text-gray-400 font-semibold mb-4">
      Filters Applied: None
    </div>


    <!-- Tabs -->
    <div class="flex justify-start mb-4 border-b border-gray-200 dark:border-gray-700">
      <button @click="setTab('all')" :class="[
        'px-4 py-2 text-sm font-medium',
        filters.tab === 'all'
          ? 'bg-white border-l border-t border-r rounded-t-md text-gray-900 dark:bg-gray-800 dark:text-white dark:border-gray-700'
          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white'
      ]">
        All Tickets ({{ allTickets }})
      </button>
      <button @click="setTab('tickets')" :class="[
        'px-4 py-2 text-sm font-medium',
        filters.tab === 'tickets'
          ? 'bg-white border-l border-t border-r rounded-t-md text-gray-900 dark:bg-gray-800 dark:text-white dark:border-gray-700'
          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white'
      ]">
        Support Tickets ({{ totalTicket }})
      </button>
      <button @click="setTab('projects')" :class="[
        'px-4 py-2 text-sm font-medium',
        filters.tab === 'projects'
          ? 'bg-white border-l border-t border-r rounded-t-md text-gray-900 dark:bg-gray-800 dark:text-white dark:border-gray-700'
          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white'
      ]">
        Project Tickets ({{ totalProject }})
      </button>
    </div>
    <!-- Data Table -->
    <div class="bg-gray-100 dark:bg-gray-800 relative shadow-md sm:rounded-lg overflow-visible">
      <div class="flex flex-col md:flex-row items-center justify-between space-y-3 md:space-y-0 md:space-x-4 p-4">
        <div class="w-full md:w-1/2">
          <form class="flex items-center" @submit.prevent="applyFilters">
            <label for="simple-search" class="sr-only">Search</label>
            <div class="relative w-full">
              <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg aria-hidden="true" class="w-5 h-5 text-gray-500 dark:text-gray-400" fill="currentColor"
                  viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clip-rule="evenodd" />
                </svg>
              </div>
              <input type="text" id="simple-search" v-model="filters.keywords" @input="debouncedFetchTickets"
                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full pl-10 p-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                placeholder="Search" />
            </div>
            <button type="submit" class="hidden">Search</button>
          </form>
        </div>
        <div
          class="w-full md:w-auto flex flex-col md:flex-row space-y-2 md:space-y-0 items-stretch md:items-center justify-end md:space-x-3 flex-shrink-0">
          <div
            class="w-full md:w-auto flex flex-col md:flex-row space-y-2 md:space-y-0 items-stretch md:items-center justify-end md:space-x-3 flex-shrink-0">
            <button @click="exportTicketsToCSV" type="button"
              class="flex items-center justify-center text-gray-100 bg-gray-400 hover:bg-yellow-500 focus:ring-4 focus:ring-primary-300 font-medium rounded-lg text-sm px-4 py-2 dark:bg-primary-600 dark:hover:bg-primary-700 focus:outline-none dark:focus:ring-primary-800">
              <svg class="h-5 w-5 text-white mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                <polyline points="16 6 12 2 8 6" />
                <line x1="12" y1="2" x2="12" y2="15" />
              </svg>
              Export
            </button>
          </div>
        </div>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400 border-separate border-spacing-y-3">
          <thead class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" class="px-4 py-3 relative">
                <button @mouseenter="showLegend = true" @mouseleave="showLegend = false"
                  class="flex items-center space-x-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 1 1-7.07 17.07A10 10 0 0 1 12 2z" />
                  </svg>
                  <span class="sr-only">Legend</span>
                </button>

                <!-- Legend Popover -->
                <div v-if="showLegend"
                  class="absolute z-50 mt-2 w-64 p-4 bg-white dark:bg-gray-700 rounded-md shadow-lg border border-gray-200 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-100">
                  <h4 class="text-md font-semibold mb-2">Ticket Urgency Colors</h4>
                  <ul class="space-y-1">
                    <li class="flex items-center">
                      <span class="inline-block w-3 h-3 mr-2 rounded-full bg-gray-500"></span>
                      Closed
                    </li>
                    <li class="flex items-center">
                      <span class="inline-block w-4 h-3 mr-2 rounded-full bg-red-500"></span>
                      Deadline Past / Unacknowledged
                    </li>
                    <li class="flex items-center">
                      <span class="inline-block w-3 h-3 mr-2 rounded-full bg-blue-500"></span>
                      Due Before Today
                    </li>
                    <li class="flex items-center">
                      <span class="inline-block w-3 h-3 mr-2 rounded-full bg-orange-500"></span>
                      Due Today
                    </li>
                    <li class="flex items-center">
                      <span class="inline-block w-3 h-3 mr-2 rounded-full bg-yellow-500"></span>
                      Due within 24 hours
                    </li>
                    <li class="flex items-center">
                      <span class="inline-block w-3 h-3 mr-2 rounded-full bg-green-500"></span>
                      Future
                    </li>
                  </ul>
                </div>
              </th>
              <th scope="col" class="px-4 py-3 cursor-pointer" @click="sortTickets('id')">ID
                <span :class="getSortClass('id')"></span>
              </th>
              <th scope="col" class="px-4 py-3">Actions</th>
              <th scope="col" class="px-4 py-3">Details</th>
              <th scope="col" class="px-4 py-3 cursor-pointer" @click="sortTickets('submitted')">Submitted
                <span :class="getSortClass('submitted')"></span>
              </th>
              <th scope="col" class="px-4 py-3 cursor-pointer" @click="sortTickets('priority')">Priority
                <span :class="getSortClass('priority')"></span>
              </th>
              <th scope="col" class="px-4 py-3 cursor-pointer" @click="sortTickets('sprint')"
                v-if="filters.tab == 'projects' && !userroles.includes(80)">Sprint
                <span :class="getSortClass('sprint')"></span>
              </th>
              <th scope="col" class="px-4 py-3 cursor-pointer" @click="sortTickets('requester')">Requested By
                <span :class="getSortClass('requester')"></span>
              </th>
              <th scope="col" class="px-4 py-3 cursor-pointer" @click="sortTickets('assigned_to')">Assigned To
                <span :class="getSortClass('assigned_to')"></span>
              </th>
              <th scope="col" class="px-4 py-3 cursor-pointer" @click="sortTickets('last_activity')">Last Activity
                <span :class="getSortClass('last_activity')"></span>
              </th>
              <th scope="col" class="px-4 py-3 cursor-pointer" @click="sortTickets('logged_time')">Hours
                <span :class="getSortClass('logged_time')"></span>
              </th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-gray-600 divide-y divide-gray-200 dark:divide-gray-700 rounded-l-xl border-r">
            <template v-for="ticket in tickets" :key="ticket.id">
              <tr class="relative overflow-hidden">
                <td class="relative pl-6 pr-4 py-2">
                  <div :class="[
                    'absolute inset-y-0 left-0 w-6 rounded-l-xl',
                    getUrgencyColorClass(ticket.urgencycolor)
                  ]"></div>
                  <button @click="toggleDetails(ticket.id)"
                    class="focus:outline-none transition-transform duration-200">
                    <svg :class="{ 'rotate-180': isDetailsVisible(ticket.id) }"
                      class="h-6 w-6 text-gray-500 dark:text-gray-400 transition-transform duration-200"
                      viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round"
                      stroke-linejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" />
                      <path d="M6 9l6 6 6-6H6z" />
                    </svg>
                  </button>
                </td>
                <td class="px-4 py-2">
                  <div class="inline-flex items-center gap-2 align-middle">
                    <button :class="[
                      'inline-flex',
                      'items-center',
                      'rounded-md',
                      'px-2',
                      'dark:bg-opacity-40',
                      'py-1',
                      'text-xs',
                      'font-medium',
                      `text-${ticket.urgencycolor}-700`,
                      `bg-${ticket.urgencycolor}-50`,
                      `ring-${ticket.urgencycolor}-700/10`,
                      'ring-1',
                      'ring-inset',
                      `dark:bg-${ticket.urgencycolor}-50/20`,
                      `dark:ring-${ticket.urgencycolor}-400/75`,
                      `dark:text-${ticket.urgencycolor}-300`
                    ]" @click="openTicket(ticket.id)">{{ ticket.id }}
                      <svg v-if="ticket.has_job_today_for_user" class="h-3 w-3 ml-1"
                        :class="ticket.is_job_running_now ? 'text-red-500' : 'text-green-500'" viewBox="0 0 8 8"
                        fill="currentColor">
                        <circle cx="4" cy="4" r="4" />
                      </svg>
                    </button>
                    <button @click="copyTicketId(ticket.id)"
                      class="text-gray-500 hover:text-yellow-500 dark:text-white focus:outline-none"
                      title="Copy Ticket ID">
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" viewBox="0 0 24 24">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                    </button>
                  </div>
                  <button v-if="ticket.acknowledged == 0 && userId == ticket.tech_id" title="Acknowledge Ticket" :class="[
                    'inline-flex',
                    'mt-2',
                    'items-center',
                    'rounded-md',
                    'px-2',
                    'py-1',
                    'text-xs',
                    'font-medium',
                    `text-green-700`,
                    `bg-green-50`,
                    `ring-green-700/10`,
                    'ring-1',
                    'ring-inset',
                    `dark:bg-green-50/20`,
                    `dark:ring-green-400/75`,
                    `dark:text-green-300`
                  ]" @click="acknowledgeTicket(ticket.id)"> Acknowledge
                  </button>
                </td>
                <td class="px-4 py-2">
                  <div class="flex items-center justify-left space-x-2">
                    <button @click="startJob(ticket.id)" title="Start Job" v-if="!userroles.includes(80)">
                      <svg class="h-5 w-5 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polygon points="10 8 16 12 10 16 10 8" />
                      </svg>
                    </button>
                    <button v-if="ticket.completed && ticket.completed !== '0000-00-00 00:00:00'" title="Reopen Ticket"
                      @click="openReopenTicketModal(ticket)">
                      <svg class="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none"
                        viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round"
                          d="M13.5 10.5V6.75a4.5 4.5 0 1 1 9 0v3.75M3.75 21.75h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H3.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                      </svg>

                    </button>
                    <button v-else title="Mark as Complete" @click="openSetCompleteTicketModal(ticket)">
                      <svg class="h-5 w-5 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    </button>
                    <button title="Transfer" @click="openTransferTicketModal(ticket.id)">
                      <svg class="h-5 w-5 text-blue-500" width="24" height="24" viewBox="0 0 24 24" stroke-width="2"
                        stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" />
                        <circle cx="5" cy="18" r="2" />
                        <circle cx="19" cy="6" r="2" />
                        <path d="M19 8v5a5 5 0 0 1 -5 5h-3l3 -3m0 6l-3 -3" />
                        <path d="M5 16v-5a5 5 0 0 1 5 -5h3l-3 -3m0 6l3 -3" />
                      </svg>
                    </button>
                    <button @click="editTicket(ticket.id)" title="Edit Ticket">
                      <svg class="h-5 w-5 text-yellow-500" width="24" height="24" viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button class="flex items-center space-x-1 hover:text-blue-500" @click.stop="openEmailModal(ticket)"
                      title="Send Email">
                      <svg class="w-5 h-5 text-blue-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
                        width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" stroke-linecap="round" stroke-width="2"
                          d="m3.5 5.5 7.893 6.036a1 1 0 0 0 1.214 0L20.5 5.5M4 19h16a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1Z" />
                      </svg>
                    </button>
                    <button @click="copyTicketUrl(ticket.id)" title="Copy Ticket URL"
                      class="text-blue-500 focus:outline-none">
                      <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" viewBox="0 0 24 24">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                    </button>
                    <div v-if="copiedTicketId === ticket.id"
                      class="absolute bottom-10 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded-md transition-opacity duration-300">
                      Copied!
                    </div>
                  </div>

                  <div v-if="isDetailsVisible(ticket.id)" class="flex justify-left space-x-2 mt-12">
                    <button @click="addJob(ticket.id)" title="Add Job" v-if="!userroles.includes(80)">
                      <svg class="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M17 14v6m-3-3h6M6 10h2a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2zm10 0h2a2 2 0 002-2V6a2 2 0 00-2-2h-2a2 2 0 00-2 2v2a2 2 0 002 2zM6 20h2a2 2 0 002-2v-2a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2z" />
                      </svg>
                    </button>
                    <button @click="toggleQuickNoteForm(ticket.id)" title="Add Note">
                      <svg class="h-5 w-5 text-green-500" width="24" height="24" viewBox="0 0 24 24" stroke-width="2"
                        stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" />
                        <line x1="13" y1="20" x2="20" y2="13" />
                        <path d="M13 20v-6a1 1 0 0 1 1 -1h6v-7a2 2 0 0 0 -2 -2h-12a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h7" />
                      </svg>
                    </button>
                    <button v-if="!userroles.includes(80)" @click="openMergeTicketModal(ticket.id)" title="Merge ticket"
                      :disabled="!isEditable" class="disabled:opacity-50 disabled:cursor-not-allowed">
                      <svg class="h-5 w-5 text-cyan-500" width="24" height="24" viewBox="0 0 24 24" stroke-width="2"
                        stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" />
                        <circle cx="7" cy="18" r="2" />
                        <circle cx="7" cy="6" r="2" />
                        <circle cx="17" cy="12" r="2" />
                        <line x1="7" y1="8" x2="7" y2="16" />
                        <path d="M7 8a4 4 0 0 0 4 4h4" />
                      </svg>
                    </button>
                    <button v-if="!userroles.includes(80)" title="Add Reminder"
                      @click="openCreateReminderModal(ticket.id)">
                      <svg class="h-5 w-5 text-blue-500" width="24" height="24" viewBox="0 0 24 24" stroke-width="2"
                        stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" />
                        <circle cx="12" cy="13" r="7" />
                        <polyline points="12 10 12 13 14 13" />
                        <line x1="7" y1="4" x2="4.25" y2="6" />
                        <line x1="17" y1="4" x2="19.75" y2="6" />
                      </svg>
                    </button>
                    <button v-if="!userroles.includes(80)" @click="deleteTicket(ticket.id)" title="Delete Ticket"
                      :disabled="!isEditable" class="disabled:opacity-50 disabled:cursor-not-allowed">
                      <svg class="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                    <button v-if="!userroles.includes(80)" @click="openTicketInvoice(ticket.id)"
                      title="Open Ticket Invoice" :disabled="!isEditable"
                      class="disabled:opacity-50 disabled:cursor-not-allowed">
                      <svg class="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                      </svg>
                    </button>
                  </div>
                </td>
                <td class="px-4 py-2 font-medium text-gray-900 dark:text-white"
                  @click="handleDetailsClick($event, ticket.id)">
                  <div>{{ ticket.redmineissueid != 0 ? ticket.redmineissueid : '' }}</div>
                  <span v-if="ticket.internal == 1" title="Internal Ticket" :class="[
                    'inline-flex',
                    'items-center',
                    'rounded-md',
                    'px-2',
                    'py-1',
                    'text-xs',
                    'font-medium',
                    `text-red-700`,
                    `bg-red-50`,
                    `ring-red-700/10`,
                    'ring-1',
                    'ring-inset',
                    `dark:bg-red-50/20`,
                    `dark:ring-red-400/75`,
                    `dark:text-red-300`
                  ]"> Internal
                  </span>
                  <div v-if="isDetailsVisible(ticket.id)">
                    <div class="markdown-body" v-html="renderMarkdown(ticket.description)"></div>
                  </div>
                  <div v-else>
                    <div class="markdown-body" v-html="renderMarkdown(ticket.desc_short)"></div>
                  </div>

                  <div v-if="isDetailsVisible(ticket.id)">
                    <div class="mb-2 p-4"><strong>AIM Instruction-Notes: </strong>
                      <div v-if="ticket.delegatornotes" v-html="renderMarkdown(ticket.delegatornotes)"></div>
                      <div v-else>None</div>
                    </div>
                    <div class="mb-4 p-4"><strong>Private Notes: </strong>
                      <div v-if="ticket.privateinfo" v-html="renderMarkdown(ticket.privateinfo)"></div>
                      <div v-else>None</div>
                    </div>
                  </div>
                  <!-- {{ ticket.ticket_type ? ticket.ticket_type.description : 'Description' }}: -->
                  <!-- {{ isDetailsVisible(ticket.id) ? (ticket.description || 'N/A') : (ticket.desc_short || '') }} -->
                </td>
                <td class="px-4 py-2 whitespace-nowrap w-16"><span>{{ ticket.submitted }} </span><br>
                  <div class="flex justify-center">
                    <span
                      class="inline-flex items-center justify-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10 dark:bg-purple-50/20 dark:ring-purple-400/75 dark:text-purple-300">
                      {{ ticket.daysold ? ticket.daysold : 'N/A' }}
                    </span>
                  </div>
                </td>
                <td class="px-4 py-2 whitespace-nowrap" :ref="`priorityCell-${ticket.id}`">
                  <div class="flex items-center justify-left space-x-2" v-click-outside="closePriorityOptions">
                    <button title="Back a day" @click="adjustPriority(ticket, -1)">
                      <svg class="h-5 w-5 text-blue-500" width="24" height="24" viewBox="0 0 24 24" stroke-width="2"
                        stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" />
                        <path d="M18 15l-6-6l-6 6h12" transform="rotate(270 12 12)" />
                      </svg>
                    </button>
                    <div class="overflow-visible relative">
                      <button @click="togglePriorityOptions(ticket.id)" class="relative w-16"
                        :ref="`priorityBtn-${ticket.id}`">{{ ticket.pdate }}</button>
                      <div v-if="showPriorityOptions === ticket.id" :id="`priority-dropdown-${ticket.id}`"
                        class="absolute z-[9999] mt-1 bg-white shadow-lg w-48 rounded-md"
                        :class="dropdownDirection[ticket.id] === 'up' ? 'bottom-full mb-1' : 'mt-1'">
                        <ul class="text-gray-700 text-xxs">
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, 'today')">Today</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, 'tomorrow')">Tomorrow</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer" @click="setPriorityDate(ticket, '2')">2
                            Days</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer" @click="setPriorityDate(ticket, '3')">3
                            Days</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer" @click="setPriorityDate(ticket, '4')">4
                            Days</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer" @click="setPriorityDate(ticket, '5')">5
                            Days</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer" @click="setPriorityDate(ticket, '6')">6
                            Days</li>

                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, '1_week')">1 Week</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, '2_weeks')">2 Weeks</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, '3_weeks')">3 Weeks</li>

                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, '1_month')">1 Month</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, '2_months')">2 Months</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, '3_months')">3 Months</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, '6_months')">6 Months</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, '9_months')">9 Months</li>

                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, '1_year')">1 Year</li>
                        </ul>
                      </div>
                    </div>
                    <button title="Add a Day" @click="adjustPriority(ticket, 1)">
                      <svg class="h-5 w-5 text-blue-500" width="24" height="24" viewBox="0 0 24 24" stroke-width="2"
                        stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" />
                        <path d="M18 15l-6-6l-6 6h12" transform="rotate(90 12 12)" />
                      </svg>
                    </button>
                  </div>
                  <div class="flex items-center justify-left space-x-2" v-click-outside="closePriorityTimeOptions">
                    <button title="Decrease Time" @click="adjustPriorityTime(ticket, -1)">
                      <svg class="h-5 w-5 text-blue-500" width="24" height="24" viewBox="0 0 24 24" stroke-width="2"
                        stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M18 15l-6-6l-6 6h12" transform="rotate(270 12 12)" />
                      </svg>
                    </button>
                    <div class="overflow-visible">
                      <button @click="togglePriorityTimeOptions(ticket.id)" :ref="`priorityTimeBtn-${ticket.id}`"
                        class="relative w-16">
                        {{ ticket.ptime }}
                      </button>
                      <div v-if="showPriorityTimeOptions === ticket.id" :id="`prioritytime-dropdown-${ticket.id}`"
                        class="absolute z-50 mt-1 bg-white shadow-lg w-48 rounded-md"
                        :class="dropdownTimeDirection[ticket.id] === 'up' ? 'bottom-full mb-1' : 'mt-1'">
                        <ul class="text-gray-700">
                          <li class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityTime(ticket, 'now')">Now</li>
                          <li class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityTime(ticket, 'next_hour')">Next Hour</li>
                          <li class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityTime(ticket, '2_hours')">+2 Hours</li>
                          <li class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityTime(ticket, 'morning')">Morning (9 AM)</li>
                          <li class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityTime(ticket, 'evening')">Evening (6 PM)</li>
                        </ul>
                      </div>
                    </div>
                    <button title="Increase Time" @click="adjustPriorityTime(ticket, 1)">
                      <svg class="h-5 w-5 text-blue-500" width="24" height="24" viewBox="0 0 24 24" stroke-width="2"
                        stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M18 15l-6-6l-6 6h12" transform="rotate(90 12 12)" />
                      </svg>
                    </button>
                  </div>
                </td>
                <td class="px-4 py-2 whitespace-nowrap" v-if="filters.tab == 'projects' && !userroles.includes(80)"
                  v-click-outside="closeSprintOptions">
                  <div class="flex items-center justify-left space-x-2">
                    <button title="Last Sprint" @click="adjustSprint(ticket, 1)">
                      <svg class="h-5 w-5 text-blue-500" width="24" height="24" viewBox="0 0 24 24" stroke-width="2"
                        stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" />
                        <path d="M18 15l-6-6l-6 6h12" transform="rotate(270 12 12)" />
                      </svg>
                    </button>
                    <button class="relative w-8" @click="toggleSprintOptions(ticket.id)">{{ ticket.ticket_sprint ?
                      ticket.ticket_sprint.name : ticket.sprint_id }}
                      <div v-if="showSprintOptions === ticket.id"
                        class="absolute z-10 mt-1 bg-white shadow-lg w-48 rounded-md">
                        <ul class="text-gray-700">
                          <li class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            @click="updateTicketSprint(ticket.id, 0)">0</li>
                        </ul>
                      </div>
                    </button>
                    <button title="Next Sprint" @click="adjustSprint(ticket, -1)">
                      <svg class="h-5 w-5 text-blue-500" width="24" height="24" viewBox="0 0 24 24" stroke-width="2"
                        stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" />
                        <path d="M18 15l-6-6l-6 6h12" transform="rotate(90 12 12)" />
                      </svg>
                    </button>
                  </div>
                </td>
                <td class="px-4 py-2 align-top text-sm text-gray-700 dark:text-gray-200 w-[15%] relative">
                  <div class="flex flex-col space-y-1" v-click-outside="() => toggleTooltip(null)">
                    <!-- Client name (red if notes/warnings exist) -->
                    <div class="text-sm font-semibold" :class="hasClientNotesOrWarnings(ticket)
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-800 dark:text-gray-100'">
                      {{ ticket.clientName }}
                    </div>

                    <!-- Button to see client notes / warnings -->
                    <button v-if="hasClientNotesOrWarnings(ticket)" @click.stop="toggleClientNotes(ticket)" class="inline-flex items-center justify-center mt-0.5 px-1 py-0.5 text-xs font-semibold rounded
                              bg-red-100 text-red-700 border border-red-400 hover:bg-red-200
                              dark:bg-red-900 dark:text-red-100 dark:border-red-500 w-fit">
                      Client Notes / Warnings
                    </button>

                    <a :href="`https://atms.aimitservices.com/addressBook.php?search=${getRequesterName(ticket)}&fname=1&lname=1`"
                      class="text-blue-600 hover:underline" target="_blank">
                      {{ getRequesterName(ticket) }}
                    </a>
                    <a v-for="(ph, phi) in getRequesterPhones(ticket)" :key="phi"
                      :href="`tel:${ph.number}${ph.ext ? ',' + ph.ext : ''}`"
                      class="text-blue-600 hover:underline">
                      {{ formatPhone(ph.number) }}
                      <span v-if="ph.ext"> x{{ ph.ext }}</span>
                      <span v-if="ph.label"> ({{ ph.label }})</span>
                    </a>
                    <a :href="`mailto:${getRequesterEmail(ticket)}`" class="text-blue-600 hover:underline"
                      target="_blank">
                      {{ getRequesterEmail(ticket) }}
                    </a>

                    <div class="inline-flex items-center justify-center mt-0.5 px-1 py-0.5 text-xs font-semibold rounded cursor-pointer 
                              bg-gray-100 text-gray-700 border border-gray-400 hover:bg-gray-200
                              dark:bg-gray-900 dark:text-gray-100 dark:border-gray-500 w-fit"
                      @click.stop="toggleTooltip(ticket.id)">
                      Delegator Info
                    </div>

                    <div v-if="isTooltipVisible(ticket.id) && (ticket.delegator_user || ticket.watcher.length > 0)"
                      class="absolute z-10 w-64 p-2 mt-1 mb-4 text-sm bg-white border border-gray-300 rounded-md shadow-md
                              dark:bg-gray-800 dark:border-gray-700 dark:text-white">
                      <p v-if="ticket.delegator_user">
                        <strong>Delegated By:</strong>
                        {{ ticket.delegator_user.firstname }} {{ ticket.delegator_user.lastname }}<br />
                        <strong>Email:</strong> {{ ticket.delegator_user.email }}
                      </p>
                      <div v-if="ticket.watcher.length">
                        <strong>Watchers:</strong>
                        <ul>
                          <li v-for="watch in ticket.watcher" :key="watch.id">
                            {{ watch.contact.name_first }} {{ watch.contact.name_last }}<br />
                            <strong>Email:</strong> {{ watch.contact.email }}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <!-- Click “popover” with client notes / warning -->
                  <div v-if="openClientNotesTicketId === ticket.id" class="mt-2 p-2 text-[11px] leading-snug bg-yellow-50 border border-yellow-300 rounded
                            text-gray-800 dark:bg-yellow-900 dark:border-yellow-600 dark:text-yellow-50">
                    <div v-if="ticket.client && ticket.client.warning && ticket.client.warning.trim() !== ''"
                      class="mb-1">
                      <div class="font-semibold text-red-700 dark:text-red-300">
                        Warning
                      </div>
                      <div class="whitespace-pre-wrap">
                        {{ ticket.client.warning }}
                      </div>
                    </div>

                    <div v-if="ticket.client && ticket.client.notes && ticket.client.notes.trim() !== ''">
                      <div class="font-semibold text-gray-800 dark:text-gray-100">
                        Client Notes
                      </div>
                      <div class="whitespace-pre-wrap">
                        {{ ticket.client.notes }}
                      </div>
                    </div>
                  </div>
                </td>
                <td class="px-4 py-2">{{ ticket.assigned_to }}</td>
                <td class="px-4 py-2 whitespace-nowrap">
                  <!-- <div class="flex flex-row space-x-1">                    </div> -->
                  <span :class="[
                    'inline-flex',
                    'items-center',
                    'rounded-md',
                    'px-2',
                    'py-1',
                    'text-xs',
                    'font-medium',
                    `text-indigo-700`,
                    `bg-indigo-50`,
                    `ring-indigo-700/10`,
                    'ring-1',
                    'ring-inset',
                    `dark:bg-indigo-50/20`,
                    `dark:ring-indigo-400/75`,
                    `dark:text-indigo-300`
                  ]" class="mb-2">{{ ticket.lastjobdate ? ticket.lastjobdate : 'N/A' }}
                  </span>
                  <div v-if="filters.tab === 'projects'">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">{{
                      ticket.ticket_status?.name || '' }}</label>
                    <label v-if="userDepartmentId == 2"
                      class="block text-sm font-medium text-gray-700 dark:text-gray-100">{{
                        ticket.ticket_code_status?.name || '' }}</label>
                  </div>
                  <div v-if="filters.tab === 'projects' && isDetailsVisible(ticket.id)"
                    class="grid grid-cols-1 sm:grid-cols-1 gap-4 mb-4">
                    <!-- Ticket Status Dropdown -->
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Ticket Status</label>
                      <select v-model="ticket.ticketstatus_id"
                        @change="updateTicketStatus(ticket.id, ticket.ticketstatus_id)"
                        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-500 dark:border-gray-900 dark:text-gray-100">
                        <option v-for="status in ticketStatuses" :key="status.id" :value="status.id">
                          {{ status.name }}
                        </option>
                      </select>
                    </div>

                    <!-- Code Status Dropdown -->
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Code Status</label>
                      <select v-model="ticket.codestatus_id"
                        @change="updateTicketCodeStatus(ticket.id, ticket.codestatus_id)"
                        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-500 dark:border-gray-900 dark:text-gray-100">
                        <option v-for="status in ticketCodeStatuses" :key="status.id" :value="status.id">
                          {{ status.name }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <!-- <span :class="getUrgencyClass(ticket.urgencycolor)">
                        {{ ticket.lastjobdate ? ticket.lastjobdate : 'N/A' }}
                      </span> -->
                  <!-- <span class="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10 dark:bg-purple-50/20 dark:ring-purple-400/75 dark:text-purple-300">{{ ticket.lastjobdate?ticket.lastjobdate:'N/A' }}</span> -->

                </td>
                <td class="px-4 py-2">
                  <span v-if="filters.tab === 'tickets'"
                    class="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10 dark:bg-blue-50/20 dark:ring-blue-400/75 dark:text-blue-300">{{
                      ticket.logged_time ? ticket.logged_time : 'N/A' }}</span>
                  <span v-else
                    class="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10 dark:bg-blue-50/20 dark:ring-blue-400/75 dark:text-blue-300">{{
                      ticket.logged_time ? ticket.logged_time : 'N/A' }}/{{ ticket.esttime ? ticket.esttime : 'N/A'
                    }}</span>
                </td>
              </tr>
              <transition name="quicknote-transition">
                <tr v-if="isQuickNoteFormVisible(ticket.id)" :key="ticket.id + '-quicknote'"
                  class="bg-gray-100 dark:bg-gray-700">
                  <td colspan="8" class="px-4 py-2">
                    <div class="p-4 rounded-md shadow-md bg-yellow-100 dark:bg-gray-900">
                      <div class="grid grid-cols-3 gap-4 mb-2">
                        <div>
                          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Time</label>
                          <input type="number" v-model="quickNoteTime" step="0.10"
                            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-500 dark:border-gray-900 dark:text-gray-100"
                            min="0">
                        </div>
                        <div>
                          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">IA</label>
                          <input type="number" v-model="quickNoteIA" step="0.10"
                            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-500 dark:border-gray-900 dark:text-gray-100"
                            min="0">
                        </div>
                        <div>
                          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100 mr-2">Publish</label>
                          <input type="checkbox" v-model="quickNotePublish"
                            class="rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
                        </div>
                      </div>
                      <div :ref="`quicknote_editor_${ticket.id}`"></div>
                      <div class="mt-4 flex justify-end space-x-2">
                        <button @click="saveQuickNote(ticket.id)"
                          class="bg-yellow-500 text-white px-4 py-2 rounded-md hover:bg-yellow-400">Save</button>
                        <button @click="toggleQuickNoteForm(ticket.id)"
                          class="bg-gray-400 text-white px-4 py-2 rounded-md hover:bg-gray-600 dark:bg-red-700 dark:hover:bg-red-500">Cancel</button>
                      </div>
                    </div>
                  </td>
                </tr>
              </transition>
              <transition name="details-transition">
                <tr v-if="isDetailsVisible(ticket.id)" :key="ticket.id + '-details'"
                  class="bg-gray-100 dark:bg-gray-700">
                  <td colspan="12" class="px-4 py-2">
                    <!-- <div class="sticky top-0 z-20 rounded-md shadow-md bg-gray-50 dark:bg-gray-900 pb-4">
                      </div> -->
                    <div class="flex flex-col md:flex-row gap-6"
                      v-if="(ticket.notes && ticket.notes.length > 0) || (ticket.documents && ticket.documents.length > 0)">
                      <div v-if="(ticket.notes && ticket.notes.length > 0)" :class="[
                        'p-4 shadow-md bg-gray-50 dark:bg-gray-900 rounded-md overflow-y-auto max-h-[800px]',
                        ticket.documents && ticket.documents.length ? 'md:w-4/5' : 'w-full'
                      ]">
                        <!-- Timeline for ticket notes -->
                        <h4 class="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">Notes</h4>
                        <ol class="relative w-full border-l border-gray-200 dark:border-gray-700">
                          <li v-for="note in ticket.notes" :key="note.id" class="mb-10 ml-6 w-full">
                            <div v-if="note">
                              <span
                                class="absolute flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full -left-4 ring-4 ring-white dark:ring-gray-900 dark:bg-blue-900">
                                <svg aria-hidden="true" class="w-3 h-3 text-blue-800 dark:text-blue-300"
                                  fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                  <path
                                    d="M20 4a2 2 0 0 0-2-2h-2V1a1 1 0 0 0-2 0v1h-3V1a1 1 0 0 0-2 0v1H6V1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v2h20V4ZM0 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8H0v10Zm5-8h10a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2Z" />
                                </svg>
                              </span>
                              <div class="flex justify-between items-center">
                                <time class="mb-1 text-sm font-normal leading-none text-gray-400 dark:text-gray-500">{{
                                  note.lastedittime != '0000-00-00 00:00:00' ? note.lastedittime : note.ts }}</time>
                              </div>
                              <h3 class="text-lg font-semibold text-gray-900 dark:text-white" v-if="note.user_profile">
                                {{ note.user_profile.firstname }} {{ note.user_profile.lastname }}</h3>
                              <h3 class="text-lg font-semibold text-gray-900 dark:text-white"
                                v-if="note.contact && !note.user_profile">{{ note.contact.name_first }} {{
                                  note.contact.name_last }}</h3>
                              <div v-if="!isNoteEditing(note.id)" class="w-full">
                                <div :id="`note-viewer-${ticket.id}-${note.id}`"></div>

                                <div class="mt-2 flex items-center gap-2">
                                  <span class="mb-4 text-xs text-gray-500 dark:text-gray-400">Total Time: {{
                                    note.totaltime }}</span>
                                  <button v-if="canEditNote(note)" @click="startEditNote(ticket, note)"
                                    class="bg-yellow-500 text-white px-3 py-1 rounded-md hover:bg-yellow-400">
                                    Edit
                                  </button>
                                </div>
                              </div>
                              <div v-else class="w-full">
                                <div class="grid grid-cols-3 gap-4 mt-3 mb-3">
                                  <div>
                                    <label
                                      class="block text-sm font-medium text-gray-700 dark:text-gray-100">Time</label>
                                    <div class="relative mt-1">
                                      <input type="text" v-model="editingNotes[note.id].time"
                                        class="block w-full pr-8 rounded-md border-gray-300 shadow-sm dark:bg-gray-500 dark:border-gray-900 dark:text-gray-100" />
                                      <div
                                        class="absolute right-1 top-1/2 transform -translate-y-1/2 flex flex-col space-y-0.5">
                                        <button type="button" @click="adjustTime(note.id, 'time', 0.05)"
                                          class="text-gray-500 hover:text-blue-500 dark:text-gray-300 dark:hover:text-blue-400">
                                          ▲
                                        </button>
                                        <button type="button" @click="adjustTime(note.id, 'time', -0.05)"
                                          class="text-gray-500 hover:text-blue-500 dark:text-gray-300 dark:hover:text-blue-400">
                                          ▼
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                  <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">IA</label>
                                    <div class="relative mt-1">
                                      <input type="text" v-model="editingNotes[note.id].ia"
                                        class="block w-full pr-8 rounded-md border-gray-300 shadow-sm dark:bg-gray-500 dark:border-gray-900 dark:text-gray-100" />
                                      <div
                                        class="absolute right-1 top-1/2 transform -translate-y-1/2 flex flex-col space-y-0.5">
                                        <button type="button" @click="adjustTime(note.id, 'ia', 0.05)"
                                          class="text-gray-500 hover:text-blue-500 dark:text-gray-300 dark:hover:text-blue-400">
                                          ▲
                                        </button>
                                        <button type="button" @click="adjustTime(note.id, 'ia', -0.05)"
                                          class="text-gray-500 hover:text-blue-500 dark:text-gray-300 dark:hover:text-blue-400">
                                          ▼
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                  <div class="flex items-end">
                                    <label
                                      class="inline-flex items-center text-sm font-medium text-gray-700 dark:text-gray-100">
                                      <input type="checkbox" v-model="editingNotes[note.id].publish"
                                        class="rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 mr-2" />
                                      Publish
                                    </label>
                                  </div>
                                </div>
                                <div :ref="`note_editor_${note.id}`"></div>

                                <div class="mt-3 flex justify-end gap-2">
                                  <button @click="saveEditNote(ticket, note)"
                                    class="bg-yellow-500 text-white px-4 py-2 rounded-md hover:bg-yellow-400">
                                    Save
                                  </button>
                                  <button @click="cancelEditNote(note.id)"
                                    class="bg-gray-400 text-white px-4 py-2 rounded-md hover:bg-gray-600 dark:bg-red-700 dark:hover:bg-red-500">
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </div>
                          </li>
                        </ol>
                      </div>
                      <div v-if="ticket.documents && ticket.documents.length"
                        class="border border-blue-300 bg-blue-50 dark:border-gray-900 dark:bg-gray-800 rounded-md m-4 p-3">
                        <h4 class="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">Attachments</h4>
                        <ul>
                          <li v-for="(doc, index) in ticket.documents" :key="index"
                            class="text-sm text-blue-600 dark:text-gray-100 flex items-center justify-between py-1">
                            <a :href="doc.presigned_url" :download="doc.doc_name" target="_blank"
                              class="hover:underline">
                              {{ doc.doc_name }}
                            </a>
                            <span class="ml-4 text-gray-600 text-xs text-right dark:text-gray-200">
                              {{ formatDateTime(doc.ts) }} &nbsp;
                              {{ doc.mime }} &nbsp;
                              {{ doc.user_profile?.firstname }} {{ doc.user_profile?.lastname }}
                            </span>
                            <button type="button"
                              class="ml-3 text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300"
                              title="Delete attachment" @click="openDeleteAttachmentModal(ticket.id, doc)">
                              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                <line x1="10" y1="11" x2="10" y2="17" />
                                <line x1="14" y1="11" x2="14" y2="17" />
                              </svg>
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </td>
                </tr>
              </transition>
            </template>
          </tbody>
        </table>
        <SendEmailModal :isVisible="showEmailModal" :ticketId="selectedEmailTicketId"
          :requesterName="selectedRequesterName" :requesterEmail="selectedRequesterEmail" :ccEmails="ccEmails"
          :ticketShortDescription="selectedTicketShortDescription" @close="showEmailModal = false"
          @send="handleEmailSend" />
        <MergeTicketsModal :isVisible="mergeTicketModal" :sourceTicketId="selectedTicketId"
          @close="mergeTicketModal = false" @complete="handleMergeTickets" />
        <CreateReminderModal :isVisible="createReminderModal" :sourceTicketId="selectedTicketId" :techs="clientTechs"
          :currentTech="selectedTech" @close="createReminderModal = false" @complete="handleCreateReminder" />
        <TransferTicket :isVisible="transferTicketModal" :sourceTicketId="selectedTicketId" :techs="clientTechs"
          :currentTech="selectedTech" @close="transferTicketModal = false" @complete="handleTicketTransfer" />
        <SetCompleteTicketModal :isVisible="setCompleteTicketModal" :ticketObj="selectedTicket"
          :requesterName="selectedRequesterName" :requesterEmail="selectedRequesterEmail"
          :requesterPhone="selectedRequesterPhone" :emailnotification="selectedTicketNotification"
          :allTypes='ticketTypes' :currentTicketTypeId="selectedTicketTypeId" :sourceTicketId="selectedTicketId"
          :lastActivity="selectedTicketLastActivity" :techs="clientTechs" :currentTech="selectedTech"
          @close="setCompleteTicketModal = false" @complete="handleCloseTicket" />
        <ReopenTicketModal :isVisible="reopenTicketModal" :sourceTicketId="selectedTicketId"
          :lastActivity="selectedTicketLastActivity" @close="reopenTicketModal = false"
          @complete="handleReopenTicket" />
        <ConfirmationModal :isVisible="showDeleteAttachmentModal" field="attachment"
          :value="deleteAttachmentDoc?.doc_name || deleteAttachmentDoc?.name || ''" @confirm="confirmDeleteAttachment"
          @cancel="cancelDeleteAttachment" />
      </div>
      <nav class="flex flex-col md:flex-row justify-between items-start md:items-center space-y-3 md:space-y-0 p-4"
        aria-label="Table navigation">
        <span class="text-sm font-normal text-gray-500 dark:text-gray-400">
          Showing
          <span class="font-semibold text-gray-900 dark:text-white">{{ startItem }}-{{ endItem }}</span>
          of
          <span class="font-semibold text-gray-900 dark:text-white">{{ totalTickets }}</span>
        </span>
        <ul class="inline-flex items-stretch -space-x-px">
          <li>
            <button @click="handlePageChange(page - 1)" :disabled="page === 1"
              class="flex items-center justify-center h-full py-1.5 px-3 ml-0 text-gray-500 bg-white rounded-l-lg border border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white">
              <span class="sr-only">Previous</span>
              <svg class="w-5 h-5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd"
                  d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                  clip-rule="evenodd" />
              </svg>
            </button>
          </li>
          <li v-for="pageNum in visiblePages" :key="pageNum">
            <button @click="handlePageChange(pageNum)"
              :class="{ 'bg-primary-50 dark:bg-gray-700 text-primary-600 dark:text-white': pageNum === page, 'hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-white': pageNum !== page }"
              class="flex items-center justify-center text-sm py-2 px-3 leading-tight text-gray-500 bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400">
              {{ pageNum }}
            </button>
          </li>
          <li>
            <button @click="handlePageChange(page + 1)" :disabled="page === totalPages"
              class="flex items-center justify-center h-full py-1.5 px-3 leading-tight text-gray-500 bg-white rounded-r-lg border border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white">
              <span class="sr-only">Next</span>
              <svg class="w-5 h-5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clip-rule="evenodd" />
              </svg>
            </button>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import MergeTicketsModal from "@/views/modals/MergeTicketsModal.vue";
import CreateReminderModal from "@/views/modals/CreateReminderModal.vue";
import SetCompleteTicketModal from '@/views/modals/SetCompleteTicketModal.vue';
import ReopenTicketModal from "@/views/modals/ReopenTicketModal.vue";
import SendEmailModal from '@/views/modals/SendEmailModal.vue';
import TransferTicket from '@/views/modals/TransferTicketModal.vue';
import { marked } from 'marked';
import FilterSidebar from '@/views/components/FilterSidebar.vue';
import Viewer from '@toast-ui/editor/dist/toastui-editor-viewer';
import '@toast-ui/editor/dist/toastui-editor-viewer.css';
import '@toast-ui/editor/dist/theme/toastui-editor-dark.css';
import '@toast-ui/editor/dist/toastui-editor.css';
import { Editor } from '@toast-ui/editor';
import ThemeService from '@/services/ThemeService';
import { useToast } from "vue-toastification";
import ConfirmationModal from "@/views/modals/ConfirmationModal.vue";
import InternalBroadcastBanner from "@/views/components/InternalBroadcastBanner.vue";
export default {
  components: {
    MergeTicketsModal,
    CreateReminderModal,
    SetCompleteTicketModal,
    SendEmailModal,
    TransferTicket,
    FilterSidebar,
    Viewer,
    ReopenTicketModal,
    ConfirmationModal,
    InternalBroadcastBanner
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      tickets: [],
      totalTickets: 0,
      totalTicket: 0,
      showLegend: false,
      copiedTicketId: null,
      allTickets: 0,
      totalProject: 0,
      userId: localStorage.getItem('user_id'),
      userDepartmentId: 'All',
      page: 1,
      itemsPerPage: 15,
      showFilters: false,
      sortKey: 'priority', // default sort column
      sortOrder: 'asc', // default sort order
      companyName: 'All',
      showClientDropdown: false,
      selectedTech: parseInt(localStorage.getItem('user_id'), 10) || 0,
      showReqDropdown: false,
      showEmailModal: false,
      selectedEmailTicketId: null,
      selectedTicket: null,
      selectedRequesterName: '',
      selectedRequesterEmail: '',
      selectedRequesterPhone: '',
      ccEmails: [],
      showReqTypeDropdown: false,
      createReminderModal: false,
      setCompleteTicketModal: false,
      showPriorityOptions: null,
      showPriorityTimeOptions: null,
      showSprintOptions: null,
      transferTicketModal: false,
      showTooltip: false,
      selectedTicketTypeId: 0,
      selectedTicketLastActivity: '',
      selectedTicketNotification: '',
      mergeTicketModal: false,
      openClientNotesTicketId: null,
      selectedTicketId: 0,
      clientsWithOpenTickets: [],
      userroles: [],
      clients: [],
      requesters: [],
      ticketTypes: [],
      ticketStatuses: [],
      ticketCodeStatuses: [],
      sprints: [],
      categories: [],
      billingTypes: [],
      allTech: [],
      allInactiveTech: [],
      clientTechs: [],
      departments: [],
      filters: {
        status: 'Open',
        date_type: '',
        client_id: 'All',
        requester_id: 'All',
        requester: 'All',
        type: 'All',
        type_id: 'All',
        category_id: 'All',
        sprint_id: 'All',
        aging: 'All',
        billingType: 'All',
        tech_id: 'All',
        department: 'All',
        startDate: '',
        endDate: '',
        keywords: '',
        pastdueonly: false,
        tab: 'tickets'
      },
      expandedTicketId: null, // Track which ticket is expanded
      debouncedFetchTickets: this.debounce(this.fetchTickets, 500),
      quickNoteFormVisible: {},
      quickNoteText: '',
      quickNoteTime: 0,
      quickNoteIA: 0,
      isInternalUser: true,
      quickNotePublish: false,
      dropdownState: {
        client: { index: -1, items: [], visible: false },
        type: { index: -1, items: [], visible: false },
        requester: { index: -1, items: [], visible: false }
      },
      invalidInputs: {
        client: false,
        requester: false,
        type: false
      },
      dropdownTimeDirection: {},
      viewers: {},
      dropdownDirection: {},
      dropdownTmeDirection: {},
      editingNotes: {},
      noteViewers: {},
      quickNoteEditors: {},
      pendingRefresh: null,
      inFlightTicketId: null,
      pointerX: 0,
      pointerY: 0,
      refreshThresholdPx: 24,
      reopenTicketModal: false,
      selectedTicketShortDescription: "",
      showDeleteAttachmentModal: false,
      deleteAttachmentTicketId: null,
      deleteAttachmentDoc: null,
      deletingAttachment: false
    };
  },
  beforeUnmount() {
    window.removeEventListener('pointermove', this.onPointerMove);
    this.clearPendingRefresh();
    Object.values(this.viewers).forEach(viewer => {
      if (viewer) viewer.destroy();
    });
    Object.values(this.editingNotes).forEach(n => {
      if (n?.editor) {
        try { n.editor.destroy(); } catch (_) { }
      }
    });
    Object.values(this.quickNoteEditors).forEach(ed => { if (ed) { try { ed.destroy(); } catch (_) { } } });
  },
  watch: {
    'filters.status'(newVal) {
      if (newVal === 'Today') {
        this.filters.tech_id = this.userId;
      }
    },
    filters: {
      deep: true,
      handler(newFilters) {
        localStorage.setItem('ticketFilters', JSON.stringify(newFilters));
      }
    },
    expandedTicketId(newTicketId) {
      this.$nextTick(() => {
        const ticket = this.tickets.find(t => t.id === newTicketId);
        if (!ticket?.notes?.length) return;

        ticket.notes.forEach(note => {
          if (this.isNoteEditing(note.id)) return;
          const content = note.description || '';
          const containerId = `note-viewer-${ticket.id}-${note.id}`;
          const el = document.getElementById(containerId);
          if (!el) return;
          this.initViewer(containerId, content);
        });

        const openNoteIds = Object.keys(this.editingNotes || {});
        if (openNoteIds.length) {
          openNoteIds.forEach(id => {
            const st = this.editingNotes[id];
            if (st?.editor) {
              this.editingNotes[id]['description'] = st.editor.getMarkdown();
              try { st.editor.destroy(); } catch (_) { }
              st.editor = null;
            }
            this.initNoteEditor(id);
          });
        }
      });
    },
    'filters.client_id': function (newVal, oldVal) {
      if (this.userroles.includes(80)) return;
      this.$updateLoading(1);
      if (newVal == 'All') {
        axios.get('requesters/')
          .then(response => {
            this.requesters = response.data;
          })
          .catch(error => {
            console.error("Error fetching all requesters:", error);
          })
          .finally(() => {
            this.$updateLoading(-1);
          });
      } else {
        if (newVal != '') {
          axios.get(`requesters/${newVal}`)
            .then(response => {
              this.requesters = response.data;
            })
            .catch(error => {
              console.error("Error fetching client-specific requesters:", error);
            })
            .finally(() => {
              this.$updateLoading(-1);
            });
        } else {
          this.$updateLoading(-1);
        }
      }
    },
    isdark(newVal) {
      if (!this.expandedTicketId) return;

      // Rebuild viewers in the new theme
      this.$nextTick(() => {
        const ticket = this.tickets.find(t => t.id === this.expandedTicketId);
        if (ticket?.notes?.length) {
          ticket.notes.forEach(note => {
            const content = note.description || '';
            const viewerContainerId = `note-viewer-${ticket.id}-${note.id}`;
            const container = document.getElementById(viewerContainerId);
            if (!container) {
              console.warn('Viewer container not found:', viewerContainerId);
              return;
            }
            if (this.viewers[viewerContainerId]) {
              this.viewers[viewerContainerId].destroy();
              delete this.viewers[viewerContainerId];
            }
            container.innerHTML = '';

            const newEl = document.createElement('div');
            newEl.id = viewerContainerId;
            container.parentNode.replaceChild(newEl, container);
            this.initViewer(viewerContainerId, content);
          });
        }

        const openNoteIds = Object.keys(this.editingNotes || {});
        if (openNoteIds.length) {
          openNoteIds.forEach(id => {
            const st = this.editingNotes[id];
            if (st?.editor) {
              this.editingNotes[id]['description'] = st.editor.getMarkdown();
              try { st.editor.destroy(); } catch (_) { }
              st.editor = null;
            }
            this.initNoteEditor(id);
          });
        }
        Object.keys(this.quickNoteFormVisible || {}).forEach(tid => {
          if (this.quickNoteFormVisible[tid]) {
            const ed = this.quickNoteEditors[tid];
            if (ed) {
              this.quickNoteText = ed.getMarkdown();
            }
            this.destroyQuickNoteEditor(tid);
            this.initQuickNoteEditor(tid);
          }
        });
      });
    }
  },
  computed: {
    groupedTechnicians() {
      return this.groupTechs(this.allTech);
    },
    groupedInactiveTechnicians() {
      return this.groupTechs(this.allInactiveTech);
    },
    appliedFilterBubbles() {
      const bubbles = [];
      const excludedKeys = ['tab', 'requester_id', 'type_id', 'status'];
      const map = {
        client_id: 'Client',
        requester: 'Requester',
        type: 'Type',
        category_id: 'Category',
        sprint_id: 'Sprint',
        aging: 'Aging',
        billingType: 'Billing Type',
        tech_id: 'Tech',
        department: 'Department',
        startDate: 'Start',
        endDate: 'End',
        date_type: 'Date Type',
        pastdueonly: 'Past Due',
        keywords: 'Search'
      };

      for (const key in this.filters) {
        const value = this.filters[key];
        if (
          !excludedKeys.includes(key) &&
          value &&
          value !== 'All' &&
          value !== false &&
          (typeof value === 'string' ? value.trim() !== '' : true)
        ) {

          let labelValue = value;
          if (key == 'client_id') {
            const client = this.clients.find(c => c.id == value);
            labelValue = client ? client.shortname || client.companyname : value;
          } else if (key == 'sprint_id') {
            const sprint = this.sprints.find(s => s.id == value);
            labelValue = sprint ? sprint.name : value;
          } else if (key == 'tech_id') {
            const tech = this.allTech.find(t => t.id == value) || this.allInactiveTech.find(t => t.id == value);
            labelValue = tech ? tech.name : value;
          } else if (key == 'category_id') {
            const category = this.categories.find(c => c.id == value);
            labelValue = category ? category.description : value;
          } else if (key == 'department') {
            const dept = this.departments.find(d => d.id == value);
            labelValue = dept ? dept.name : value;
          } else if (key === 'keywords') {
            labelValue = value.trim();
          }
          bubbles.push({
            key,
            label: `${map[key] || key}: ${labelValue}`
          });
        }
      }

      return bubbles;
    },
    ticketCount() {
      return this.filters.tab === 'tickets' ? this.totalTicket : 0;
    },
    projectCount() {
      return this.filters.tab === 'projects' ? this.totalProject : 0;
    },
    filteredClients() {
      // return this.companyName
      //   ? this.clients.filter((e) =>
      //     e.companyname
      //       .toLowerCase()
      //       .includes(this.companyName.toLowerCase())
      //   )
      //   : this.clients;
      const filtered = this.companyName
        ? this.clients.filter((e) =>
          e.companyname
            .toLowerCase()
            .includes(this.companyName.toLowerCase())
        )
        : this.clients;
      this.dropdownState.client.items = filtered;
      return filtered;
    },
    filteredRequesters() {
      // return this.filters.requester
      //   ? this.requesters.filter((e) =>
      //       e.name.toLowerCase().includes(this.filters.requester.toLowerCase())
      //     ).slice(0, 20)
      //   : this.requesters;
      const filtered = this.filters.requester
        ? this.requesters.filter((e) =>
          e.name.toLowerCase().includes(this.filters.requester.toLowerCase())
        ).slice(0, 20)
        : this.requesters;
      this.dropdownState.requester.items = filtered;
      return filtered;
    },
    filteredReqTypes() {
      // return this.filters.type
      //   ? this.ticketTypes.filter((e) =>
      //     e.description
      //       .toLowerCase()
      //       .includes(this.filters.type.toLowerCase())
      //   )
      //   : this.ticketTypes;
      const filtered = this.filters.type
        ? this.ticketTypes.filter((e) =>
          e.description
            .toLowerCase()
            .includes(this.filters.type.toLowerCase())
        )
        : this.ticketTypes;
      this.dropdownState.type.items = filtered;
      return filtered;
    },
    totalPages() {
      return Math.ceil(this.totalTickets / this.itemsPerPage);
    },
    visiblePages() {
      const pages = [];
      if (this.totalPages <= 5) {
        for (let i = 1; i <= this.totalPages; i++) {
          pages.push(i);
        }
      } else {
        if (this.page <= 3) {
          pages.push(1, 2, 3, 4, '...', this.totalPages);
        } else if (this.page > this.totalPages - 3) {
          pages.push(1, '...', this.totalPages - 3, this.totalPages - 2, this.totalPages - 1, this.totalPages);
        } else {
          pages.push(1, '...', this.page - 1, this.page, this.page + 1, '...', this.totalPages);
        }
      }
      return pages;
    },
    startItem() {
      return (this.page - 1) * this.itemsPerPage + 1;
    },
    endItem() {
      return Math.min(this.page * this.itemsPerPage, this.totalTickets);
    },
    isEditable() {
      return this.userroles.some(r => [10, 20].includes(r));
    },
    isdark() {
      return ThemeService.isDark.value;
    }
  },
  methods: {
    // Groups a flat tech list into department buckets for the Tech filter's
    // <optgroup>s. Shared by the active and inactive tech dropdowns. External
    // techs get their company appended and are sorted to the end; techs with no
    // department fall into an unlabeled group at the top.
    groupTechs(list) {
      const groups = {};
      const ungrouped = [];

      (list || []).forEach(tech => {
        if (!tech.deptId) {
          // No deptId at all → push to ungrouped
          ungrouped.push({
            id: tech.id,
            name: tech.name,
          });
          return;
        }

        let groupKey = '';

        if (tech.deptId === "External Tech") {
          groupKey = "External Tech";
        } else if (tech.details) {
          groupKey = tech.details;
        } else {
          groupKey = "Other Department";
        }

        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }

        // Append company name for external techs
        let displayName = tech.name;
        if (tech.deptId === "External Tech" && tech.details) {
          displayName += ` (${tech.details})`;
        }

        groups[groupKey].push({
          id: tech.id,
          name: displayName,
        });
      });

      // If ungrouped exist, add as a group at the top
      if (ungrouped.length > 0) {
        groups[""] = ungrouped; // Empty string so it doesn't show a group label
      }

      // Get keys and sort
      let keys = Object.keys(groups).filter(k => k !== "External Tech").sort((a, b) => {
        if (a === "") return -1; // Keep ungrouped first
        if (b === "") return 1;
        return a.localeCompare(b);
      });

      // Add External Tech last
      if (groups["External Tech"]) {
        keys.push("External Tech");
      }

      // Rebuild ordered groups object
      const orderedGroups = {};
      keys.forEach(key => {
        orderedGroups[key] = groups[key];
      });

      return orderedGroups;
    },
    async init() {
      this.$updateLoading(1);
      const savedFilters = localStorage.getItem('ticketFilters');
      const roleResponse = await axios.get(`user/role/${this.userId}`);
      this.userroles = roleResponse.data.roles;
      if (savedFilters) {
        try {
          this.filters = JSON.parse(savedFilters);
        } catch (e) {
          console.error('Error parsing saved filters:', e);
        }
      } else if (!this.userroles.includes(80)) {
        this.filters.tech_id = localStorage.getItem('user_id');
        this.filters.department = this.userDepartmentId;
      }
      try {
        const [userCompanyResponse, userprofile] = await Promise.all([
          axios.get(`user/company/${this.userId}`),
          axios.get(`user/profile/${this.userId}`),
        ]);
        this.isInternalUser = userCompanyResponse.data.length === 0;

        console.log('User is ' + (this.isInternalUser ? 'internal' : 'external'));
        this.userDepartmentId = userprofile.data.aimdept;
        this.itemsPerPage = userprofile.data.ticketsperpage ?? 15;
      } catch (error) {
        this.isInternalUser = true;
      }
      try {
        axios.get('clients/')
          .then(response => {
            this.clients = response.data;
            if (this.filters.client_id && this.filters.client_id !== 'All' && this.filters.client_id !== '') {
              console.log('client id', this.filters.client_id);
              const client = this.clients.find(c => c.id == this.filters.client_id);
              console.log('client', client);
              this.companyName = client ? client.companyname : 'All';
            } else {
              this.companyName = 'All';
            }
          })
          .catch(error => {
            console.error(error);
          });
        axios.get(`clients/open-tickets/${this.userId}`)
          .then(response => {
            this.clientsWithOpenTickets = response.data;
          })
          .catch(error => {
            console.error(error);
          });
        axios.get('requesters/')
          .then(response => {
            this.requesters = response.data;
          })
          .catch(error => {
            console.error(error);
          });

        axios.get('departments/')
          .then(response => {
            this.departments = response.data;
          })
          .catch(error => {
            console.error(error);
          });

        axios.get(`user/techs`)
          .then((response) => {
            this.allTech = response.data;
          })
          .catch((error) => {
            console.error("There was an error fetching ticket techs:", error);
          });

        axios.get(`user/techs`, { params: { status: 'inactive' } })
          .then((response) => {
            this.allInactiveTech = response.data;
          })
          .catch((error) => {
            console.error("There was an error fetching inactive ticket techs:", error);
          });
        let newVal = 30
        if (this.filters.client_id != '' && this.filters.client_id != 'All') {
          newVal = this.filters.client_id;
        }
        axios.get(`ticket/get-types/${newVal}`)
          .then((response) => {
            this.ticketTypes = response.data;
          })
          .catch((error) => {
            console.error("There was an error fetching ticket types:", error);
          });
        axios.get(`ticket/status`)
          .then((response) => {
            this.ticketStatuses = response.data;
          })
          .catch((error) => {
            console.error("There was an error fetching ticket types:", error);
          });
        axios.get(`ticket/codestatus`)
          .then((response) => {
            this.ticketCodeStatuses = response.data;
          })
          .catch((error) => {
            console.error("There was an error fetching ticket types:", error);
          });
        axios.get(`ticket/sprint`)
          .then((response) => {
            this.sprints = response.data;
          })
          .catch((error) => {
            console.error("There was an error fetching ticket types:", error);
          });
        axios.get(`ticket/get-categories/${newVal}`)
          .then((response) => {
            this.categories = response.data;
          })
          .catch((error) => {
            console.error("There was an error fetching ticket categories:", error);
          });

        axios.get(`clients/billing-types/`)
          .then((response) => {
            this.billingTypes = response.data.billingtypes;
          })
          .catch((error) => {
            console.error("There was an error fetching billing types:", error);
          });
      } catch (error) {
        console.error('Error fetching tickets:', error);
      } finally {
        this.$updateLoading(-1);
      }
    },
    async setPriorityDate(ticket, dayOption) {
      // Assuming you have a method to calculate the date based on the option
      const newDate = this.calculateNewDate(dayOption, ticket.ptime);
      await this.updateTicketPriorityDate(ticket.id, newDate);
      this.showPriorityOptions = null; // Close the dropdown
    },
    async adjustSprint(ticket, adjustment) {
      const currentIndex = this.sprints.findIndex(s => s.id === ticket.sprint_id);
      if (currentIndex === -1) return;

      const newIndex = currentIndex + adjustment;

      // Make sure the new index is within range
      if (newIndex < 0 || newIndex >= this.sprints.length) return;

      const newSprint = this.sprints[newIndex];
      if (!newSprint) return;

      await this.updateTicketSprint(ticket.id, newSprint.id);
    },
    async updateTicketSprint(ticketId, sprint_id) {
      this.$updateLoading(1);
      try {
        const response = await axios.post('ticket/update-sprint', { ticketId, sprint_id });
        this.showSprintOptions = null;
        this.fetchTickets();
      } catch (error) {
        console.error('Error updating sprint:', error);
      } finally {
        this.$updateLoading(-1);
      }
    },
    async updateTicketStatus(ticketId, ticketstatus_id) {
      this.$updateLoading(1);
      try {
        const response = await axios.post('ticket/update-status', { ticketId, ticketstatus_id });
        this.fetchTickets();
      } catch (error) {
        console.error('Error updating status:', error);
      } finally {
        this.$updateLoading(-1);
      }
    },
    async updateTicketCodeStatus(ticketId, codestatus_id) {
      this.$updateLoading(1);
      try {
        const response = await axios.post('ticket/update-codestatus', { ticketId, codestatus_id });
        this.fetchTickets();
      } catch (error) {
        console.error('Error updating codestatus:', error);
      } finally {
        this.$updateLoading(-1);
      }
    },
    async adjustPriority(ticket, adjustment) {
      const adjustedDate = this.adjustDateByDays(ticket.pdate, adjustment, ticket.ptime);
      await this.updateTicketPriorityDate(ticket.id, adjustedDate);
    },
    async updateTicketPriorityDate(ticketId, newDate) {
      this.$updateLoading(1);
      this.inFlightTicketId = ticketId;
      try {
        const response = await axios.post('ticket/update-priority', { ticketId, newDate });
        let [datePart, timePart] = newDate.split('T');
        const shortYearDate = `${datePart.slice(2)}`;
        const ticketIndex = this.tickets.findIndex(ticket => ticket.id === ticketId);
        if (ticketIndex !== -1) {
          this.tickets[ticketIndex].pdate = shortYearDate;
        }
        this.deferRefreshForCell(ticketId)
      } catch (error) {
        console.error('Error updating priority date:', error);
      } finally {
        this.inFlightTicketId = null;
        this.$updateLoading(-1);
      }
    },
    async copyTicketId(id) {
      try {
        await navigator.clipboard.writeText(id.toString());
        this.copiedTicketId = id;
        setTimeout(() => (this.copiedTicketId = null), 1000);
      } catch (err) {
        console.error('Copy failed:', err);
      }
    },
    hasClientNotesOrWarnings(ticket) {
      const c = ticket.client;
      if (!c) return false;

      const hasWarning = c.warning && c.warning.trim() !== '';
      const hasNotes = c.notes && c.notes.trim() !== '';
      return hasWarning || hasNotes;
    },

    toggleClientNotes(ticket) {
      this.openClientNotesTicketId =
        this.openClientNotesTicketId === ticket.id ? null : ticket.id;
    },
    initViewersForTicket(ticketId) {
      this.$nextTick(() => {
        const ticket = this.tickets.find(t => t.id === ticketId);
        if (!ticket?.notes?.length) return;

        ticket.notes.forEach(note => {
          if (this.isNoteEditing(note.id)) return;

          const containerId = `note-viewer-${ticket.id}-${note.id}`;
          const el = document.getElementById(containerId);
          if (!el) return;

          this.initViewer(containerId, note.description || '');
        });
      });
    },
    openDeleteAttachmentModal(ticketId, doc) {
      this.deleteAttachmentTicketId = ticketId;
      this.deleteAttachmentDoc = doc;
      this.showDeleteAttachmentModal = true;
    },

    async confirmDeleteAttachment() {
      if (!this.deleteAttachmentTicketId || !this.deleteAttachmentDoc) return;

      const docId = this.deleteAttachmentDoc.id ?? this.deleteAttachmentDoc.document_id;
      if (!docId) {
        this.showDeleteAttachmentModal = false;
        return;
      }

      this.deletingAttachment = true;

      try {
        await axios.post("ticket/attachment/delete", {
          ticket_id: this.deleteAttachmentTicketId,
          document_id: docId,
        });

        const ticketIndex = this.tickets?.findIndex(
          t => String(t.id ?? t.ID) === String(this.deleteAttachmentTicketId)
        );

        if (ticketIndex !== -1 && this.tickets[ticketIndex]?.documents?.length) {
          this.tickets[ticketIndex].documents = this.tickets[ticketIndex].documents.filter(d => {
            const did = d.id ?? d.document_id;
            return String(did) !== String(docId);
          });
        }
      } catch (e) {
        console.error("Delete attachment failed:", e);
      } finally {
        this.deletingAttachment = false;
        this.showDeleteAttachmentModal = false;
        this.deleteAttachmentTicketId = null;
        this.deleteAttachmentDoc = null;
      }
    },

    cancelDeleteAttachment() {
      if (this.deletingAttachment) return;
      this.showDeleteAttachmentModal = false;
      this.deleteAttachmentTicketId = null;
      this.deleteAttachmentDoc = null;
    },
    getUrgencyColorClass(color) {
      const map = {
        red: 'bg-red-500',
        green: 'bg-green-500',
        yellow: 'bg-yellow-500',
        blue: 'bg-blue-500',
        purple: 'bg-purple-500',
        orange: 'bg-orange-500',
        gray: 'bg-gray-500'
        // Add all used urgency colors
      };
      return map[color] || 'bg-gray-400';
    },
    onKeyDown(field, direction) {
      const state = this.dropdownState[field];
      if (!state.visible || state.items.length === 0) return;

      if (direction === 'down') {
        state.index = (state.index + 1) % state.items.length;
      } else if (direction === 'up') {
        state.index = (state.index - 1 + state.items.length) % state.items.length;
      }
      this.$nextTick(() => {
        const container = this.$el.querySelector(`.scrollable-requester-list[data-field="${field}"]`);
        const activeItem = container?.querySelector('.active-item');
        activeItem?.scrollIntoView({ block: 'nearest' });
      });
    },

    onEnter(field, selectFn, nextRef = null, ...args) {
      const state = this.dropdownState[field];
      if (state.index >= 0 && state.index < state.items.length) {
        selectFn(state.items[state.index], ...args);
        state.index = -1;
        if (nextRef) {
          this.$nextTick(() => this.$refs[nextRef]?.focus());
        }
      }
    },
    async handleEmailSend(payload) {
      this.$updateLoading(1);
      try {
        await axios.post('job/send-email', {
          job_id: payload.jobId ? payload.jobId : 0,
          ticket_id: payload.ticketId ? payload.ticketId : 0,
          subject: payload.subject,
          to: payload.to,
          cc: payload.cc,
          body: payload.body,
        });
        this.showEmailModal = false;
        this.toast.success("Email sent successfully!");
        await this.fetchTickets();
      } catch (err) {
        console.error("Error sending email:", err);
        this.toast.error("Failed to send email.");
      } finally {
        this.$updateLoading(-1);
      }
    },
    handleDetailsClick(e, id) {
      // If user selected some text, don't toggle
      const sel = window.getSelection?.();
      if (sel && sel.toString().trim().length > 0) return;

      // If click originated on interactive elements, don't toggle
      if (e.target.closest('a, button, input, textarea, select, svg')) return;

      this.toggleDetails(id);
    },
    openEmailModal(ticket) {
      this.selectedEmailTicketId = ticket.id;
      this.selectedRequesterName = this.getRequesterName(ticket);
      this.selectedRequesterEmail = this.getRequesterEmail(ticket);
      const rawDesc = ticket.desc_short || "";
      const cleanedDesc = rawDesc
        .replace(/[\r\n]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      this.selectedTicketShortDescription = cleanedDesc;
      let ccList = [];
      if (ticket.watcher && Array.isArray(ticket.watcher)) {
        ccList = ticket.watcher
          .map(w => w.contact?.email)
          .filter(email => !!email);
      }

      this.ccEmails = ccList;
      this.showEmailModal = true;
    },

    getRequesterName(ticket) {
      if (ticket.contact) {
        return ticket.contact.name;
      } else if (ticket.requester) {
        return ticket.requester;
      }
      return '';
    },

    getRequesterEmail(ticket) {
      if (ticket.contact) {
        return ticket.contact.email;
      } else if (ticket.email) {
        return ticket.email;
      }
      return '';
    },
    canEditNote(note) {
      const noteUserId = Number(note.user_id ?? 0);
      const userId = Number(this.userId ?? 0);
      return noteUserId > 0 && userId > 0 && noteUserId === userId;
    },
    // All of a requester's phone numbers (primary + secondary), falling back to the
    // ticket's alternate phone when the contact has none.
    getRequesterPhones(ticket) {
      const contact = ticket.contact ?? false;
      const phones = [];

      if (contact) {
        if (contact.phone_primary) {
          phones.push({ number: contact.phone_primary, label: 'Primary', ext: contact.phone_primary_ext || '' });
        }
        if (contact.phone_secondary) {
          phones.push({ number: contact.phone_secondary, label: 'Secondary', ext: contact.phone_secondary_ext || '' });
        }
      }

      if (!phones.length && ticket.alternatephone) {
        phones.push({ number: ticket.alternatephone, label: 'Alternate', ext: '' });
      }

      return phones;
    },
    getRequesterPhone(ticket) {
      return this.getRequesterPhones(ticket)[0] || { number: '', label: '', ext: '' };
    },
    formatPhone(phone) {
      if (!phone) return '';

      const raw = String(phone).trim();
      let digits = raw.replace(/\D/g, '');

      if (digits.length === 11 && digits.startsWith('1')) {
        digits = digits.slice(1);
      }
      if (digits.length === 10) {
        const area = digits.slice(0, 3);
        const prefix = digits.slice(3, 6);
        const line = digits.slice(6);
        return `(${area}) ${prefix}-${line}`;
      }
      return raw;
    },
    resetDropdownState(field) {
      this.dropdownState[field].index = -1;
      this.dropdownState[field].visible = false;
    },
    formatDateTime(datetime) {
      const date = new Date(datetime);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    clearSpecificFilter(key) {
      if (key in this.filters) {
        if (key === 'keywords') {
          this.filters.keywords = '';
        }
        this.filters[key] = key === 'pastdueonly' ? false : 'All';

        // Reset special fields to empty string
        if (['startDate', 'endDate', 'keywords'].includes(key)) {
          this.filters[key] = '';
        }

        // Reset helper fields
        if (key === 'client_id') {
          this.companyName = 'All';
        }
        if (key === 'requester') {
          this.filters.requester_id = 'All';
        }
        if (key === 'type') {
          this.filters.type_id = 'All';
        }

        this.fetchTickets();
      }
    },
    addMonths(date, n) {
      const d = new Date(date.getTime());
      const day = d.getDate();
      d.setMonth(d.getMonth() + n);
      if (d.getDate() < day) {
        d.setDate(0);
      }
      return d;
    },
    calculateNewDate(dayOption, ptime) {
      const today = new Date();
      let newDate;
      switch (dayOption) {
        case 'today':
          newDate = today;
          break;
        case 'tomorrow':
          newDate = new Date(today.setDate(today.getDate() + 1));
          break;
        case '2':
          newDate = new Date(today.setDate(today.getDate() + 2));
          break;
        case '3':
          newDate = new Date(today.setDate(today.getDate() + 3));
          break;
        case '4':
          newDate = new Date(today.setDate(today.getDate() + 4));
          break;
        case '5':
          newDate = new Date(today.setDate(today.getDate() + 5));
          break;
        case '6':
          newDate = new Date(today.setDate(today.getDate() + 6));
          break;
        case 'next_week':
        case '1_week':
          newDate = new Date(today.setDate(today.getDate() + 7));
          break;
        case 'two_week':
        case '2_weeks':
          newDate = new Date(today.setDate(today.getDate() + 14));
          break;
        case '3_weeks':
          newDate = new Date(today.setDate(today.getDate() + 21));
          break;
        // months
        case '1_month':
          newDate = this.addMonths(today, 1);
          break;
        case '2_months':
          newDate = this.addMonths(today, 2);
          break;
        case '3_months':
          newDate = this.addMonths(today, 3);
          break;
        case '6_months':
          newDate = this.addMonths(today, 6);
          break;
        case '9_months':
          newDate = this.addMonths(today, 9);
          break;
        // year
        case '1_year':
          newDate = this.addMonths(today, 12);
          break;
        default:
          newDate = today;
      }
      const localDate = newDate.toLocaleDateString('en-CA');
      return `${localDate}T${ptime}`;
    },
    adjustDateByDays(currentDate, days, ptime) {
      const parts = currentDate.split('-'); // 'YY-MM-DD'
      if (parts.length !== 3) return null;

      const [yy, mm, dd] = parts;
      // If you might sometimes get 4-digit years, handle both:
      const year = (yy.length === 2) ? 2000 + parseInt(yy, 10) : parseInt(yy, 10);
      const month = parseInt(mm, 10) - 1; // 0-based
      const day = parseInt(dd, 10);

      // Use local-time constructor to avoid UTC parsing issues
      const date = new Date(year, month, day);
      date.setDate(date.getDate() + Number(days || 0));

      // Format YYYY-MM-DD
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');

      return `${y}-${m}-${d}T${ptime}`;
    },
    closePriorityOptions() {
      this.showPriorityOptions = null;
    },
    closePriorityTimeOptions() {
      this.showPriorityTimeOptions = null;
    },
    closeSprintOptions() {
      this.showSprintOptions = null;
    },
    togglePriorityOptions(ticketId) {
      if (this.showPriorityOptions === ticketId) {
        this.showPriorityOptions = null;
        return;
      }

      this.$nextTick(() => {
        const btn = this.$refs[`priorityBtn-${ticketId}`]?.[0];
        if (!btn) return;

        const rect = btn.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        this.dropdownDirection = {
          ...this.dropdownDirection,
          [ticketId]: spaceBelow < 300 && spaceAbove > 300 ? 'up' : 'down'
        };

        this.showPriorityOptions = ticketId;
      });
    },
    togglePriorityTimeOptions(ticketId) {
      if (this.showPriorityTimeOptions === ticketId) {
        this.showPriorityTimeOptions = null;
        return;
      }

      this.$nextTick(() => {
        const btn = this.$refs[`priorityTimeBtn-${ticketId}`]?.[0];
        if (!btn) return;

        const rect = btn.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        this.dropdownTimeDirection = {
          ...this.dropdownTimeDirection,
          [ticketId]: spaceBelow < 300 && spaceAbove > 300 ? 'up' : 'down'
        };

        this.showPriorityTimeOptions = ticketId;
      });
    },
    toggleSprintOptions(ticketId) {
      this.showSprintOptions = this.showSprintOptions === ticketId ? null : ticketId;
    },
    getRectsForTicket(ticketId) {
      const rects = [];
      const cellRef = this.$refs[`priorityCell-${ticketId}`];
      const cellEl = Array.isArray(cellRef) ? cellRef[0] : cellRef;
      if (cellEl) rects.push(cellEl.getBoundingClientRect());

      const dd1 = document.getElementById(`priority-dropdown-${ticketId}`);
      if (dd1) rects.push(dd1.getBoundingClientRect());
      const dd2 = document.getElementById(`prioritytime-dropdown-${ticketId}`);
      if (dd2) rects.push(dd2.getBoundingClientRect());

      return rects;
    },

    getBufferedUnionRect(rects, threshold) {
      if (!rects.length) return null;
      const left = Math.min(...rects.map(r => r.left)) - threshold;
      const right = Math.max(...rects.map(r => r.right)) + threshold;
      const top = Math.min(...rects.map(r => r.top)) - threshold;
      const bottom = Math.max(...rects.map(r => r.bottom)) + threshold;
      return { left, right, top, bottom };
    },
    onPointerMove(e) {
      this.pointerX = e.clientX;
      this.pointerY = e.clientY;
      this.maybeRunDeferredRefresh();
    },

    deferRefreshForCell(ticketId, threshold = this.refreshThresholdPx) {
      this.pendingRefresh = { ticketId, threshold };
    },

    maybeRunDeferredRefresh() {
      if (!this.pendingRefresh) return;
      if (this.inFlightTicketId && this.inFlightTicketId === this.pendingRefresh.ticketId) return;

      const { ticketId, threshold } = this.pendingRefresh;
      const rects = this.getRectsForTicket(ticketId);
      const region = this.getBufferedUnionRect(rects, threshold || 24);
      if (!region) return;

      const outside =
        this.pointerX < region.left ||
        this.pointerX > region.right ||
        this.pointerY < region.top ||
        this.pointerY > region.bottom;

      if (outside) this.runPendingRefreshNow();
    },

    runPendingRefreshNow() {
      if (!this.pendingRefresh) return;
      this.clearPendingRefresh();
      this.fetchTickets();
    },

    clearPendingRefresh() {
      this.pendingRefresh = null;
    },
    async setPriorityTime(ticket, timeOption) {
      const newTime = this.calculateNewTime(timeOption);
      await this.updateTicketPriorityTime(ticket.id, newTime);
      this.showPriorityTimeOptions = null; // Close the dropdown
    },
    async adjustPriorityTime(ticket, adjustment) {
      const adjustedTime = this.adjustTimeByHours(ticket.ptime, adjustment);
      await this.updateTicketPriorityTime(ticket.id, adjustedTime);
    },
    async updateTicketPriorityTime(ticketId, newTime) {
      this.$updateLoading(1);
      this.inFlightTicketId = ticketId;
      try {
        const ticket = this.tickets.find(t => t.id === ticketId);
        if (!ticket || !ticket.pdate) {
          throw new Error(`Ticket with id ${ticketId} or its date not found.`);
        }

        // Combine the existing date with the updated time
        const newDate = `${ticket.pdate}T${newTime}`;
        const response = await axios.post('ticket/update-priority', { ticketId, newDate });
        const ticketIndex = this.tickets.findIndex(ticket => ticket.id === ticketId);
        if (ticketIndex !== -1) {
          this.tickets[ticketIndex].ptime = newTime;
        }
        this.deferRefreshForCell(ticketId)
      } catch (error) {
        console.error('Error updating priority time:', error);
      } finally {
        this.inFlightTicketId = null;
        this.$updateLoading(-1);
      }
    },
    calculateNewTime(timeOption) {
      const now = new Date();
      let newTime;
      switch (timeOption) {
        case 'now':
          newTime = now.toTimeString().slice(0, 5);
          break;
        case 'next_hour':
          newTime = new Date(now.setHours(now.getHours() + 1)).toTimeString().slice(0, 5);
          break;
        case '2_hours':
          newTime = new Date(now.setHours(now.getHours() + 2)).toTimeString().slice(0, 5);
          break;
        case 'morning':
          newTime = '09:00';
          break;
        case 'evening':
          newTime = '18:00';
          break;
        default:
          newTime = now.toTimeString().slice(0, 5);
      }
      return newTime;
    },
    adjustTimeByHours(currentTime, hours) {
      const [currentHour, currentMinute] = currentTime.split(':').map(Number);
      const newHour = (currentHour + hours + 24) % 24;
      return `${String(newHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;
    },
    preprocessMarkdown(markdownText) {
      // Split the Markdown text into lines
      const lines = markdownText.split('\n');
      const processedLines = lines.map(line => {
        // Check if the line starts with spaces followed by a dash (list item)
        // and replace leading spaces with a tab
        return line.replace(/^(\s+)-/, '\t-');
      });
      // Join the processed lines back into a single string
      return processedLines.join('\n');
    },
    renderMarkdown(text) {
      const html = marked.parse(this.preprocessMarkdown(text));
      // console.log(html);
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      doc.querySelectorAll('h2').forEach(el => {
        el.classList.add('mb-2', 'text-lg', 'font-semibold', 'text-gray-900', 'dark:text-white');
      });
      doc.querySelectorAll('h3, p').forEach(el => {
        el.classList.add('mb-2', 'text-base', 'font-semibold', 'text-gray-900', 'dark:text-white');
      });

      // Add classes to unordered lists and list items
      // doc.querySelectorAll('ul').forEach(ul => {
      //   ul.classList.add('max-w-md', 'space-y-1', 'text-gray-500', 'list-disc', 'list-inside', 'dark:text-gray-400');
      //   ul.querySelectorAll('li').forEach(li => {
      //     li.classList.add('list-item-class'); // add if specific LI classes are needed
      //   });
      // });

      // Style for all unordered lists
      doc.querySelectorAll('ul').forEach(ul => {
        ul.classList.add('max-w-xl', 'space-y-1', 'text-gray-500', 'list-disc', 'list-inside', 'dark:text-gray-400');
      });

      // Additional style for nested lists
      doc.querySelectorAll('ul ul').forEach(nestedUl => {
        nestedUl.classList.add('ml-4', 'border-l', 'pl-4', 'mt-2', 'border-gray-200', 'dark:border-gray-700');
      });

      // Return the modified HTML as a string
      return doc.body.innerHTML;
    },
    toggleTooltip(ticketId) {
      if (this.showTooltip === ticketId) {
        this.showTooltip = null;
      } else {
        this.showTooltip = ticketId;
      }
    },
    isTooltipVisible(ticketId) {
      return this.showTooltip === ticketId;
    },
    async fetchTickets() {
      this.$updateLoading(1);
      const params = {
        page: this.page,
        itemsPerPage: this.itemsPerPage,
        sort_column: this.sortKey,
        sort_order: this.sortOrder.toUpperCase(),
        ...this.filters
      };

      try {
        // Your fetch logic here
        axios.post('ticket/getAll', params)
          .then(response => {
            this.tickets = response.data.data.data ? response.data.data.data : [];
            this.totalTickets = response.data.data.total;
            this.totalTicket = response.data.tickets_tab_count;
            this.allTickets = response.data.all_tab_count;
            this.totalProject = response.data.projects_tab_count;
            if (this.expandedTicketId) {
              this.initViewersForTicket(this.expandedTicketId);
              const openNoteIds = Object.keys(this.editingNotes || {});
              if (openNoteIds.length) {
                openNoteIds.forEach(id => {
                  const st = this.editingNotes[id];
                  if (st?.editor) {
                    this.editingNotes[id]['description'] = st.editor.getMarkdown();
                    try { st.editor.destroy(); } catch (_) { }
                    st.editor = null;
                  }
                  this.initNoteEditor(id);
                });
              }
            }
          })
          .catch(error => {
            console.error(error);
          }).finally(() => {
            this.$updateLoading(-1);
          });
      } catch (error) {
        console.error('Error fetching tickets:', error);
      }
    },
    async saveQuickNote(ticketId) {
      try {
        this.$updateLoading(1);
        const ticket = this.tickets.find(t => t.id === ticketId);
        if (!ticket) {
          throw new Error(`Ticket with id ${ticketId} not found`);
        }
        const ed = this.quickNoteEditors[ticketId];
        const description = ed ? ed.getMarkdown() : (this.quickNoteText || '');
        const response = await axios.post('job/add-note', {
          client_id: ticket.company_id,
          ticket_id: ticket.id,
          contact_id: ticket.contact_id,
          description: description,
          time: this.quickNoteTime,
          ia: this.quickNoteIA,
          publish: this.quickNotePublish ? 1 : 0
        });
        console.log(response);
        this.toggleQuickNoteForm(ticketId);
        await this.fetchTickets();
        this.initViewersForTicket(ticketId);
      } catch (error) {
        console.error('Error saving quick note:', error);
      } finally {
        this.$updateLoading(-1);
      }
    },
    handleMergeTickets({ sourceTicketId, targetTicketId, reason }) {
      this.$updateLoading(1);
      axios.post('ticket/merge', { sourceTicketId: sourceTicketId, targetTicketId: targetTicketId, reason: reason })
        .then(response => {
          this.fetchTickets(); // Refresh the tickets list
        })
        .catch(error => {
        }).finally(() => {
          this.$updateLoading(-1);
        });
    },
    handleCreateReminder({ ticketId, techId, day, time, sendText, description }) {
      this.$updateLoading(1);
      const ticket = this.tickets.find(t => t.id === ticketId);
      if (!ticket) {
        throw new Error(`Ticket with id ${ticketId} not found`);
      }
      axios.post('ticket/create-reminder', {
        ticketId: ticketId,
        techId: techId,
        day: day,
        time: time,
        sendText: sendText ? 1 : 0,
        description: description,
        contact_id: ticket.contact_id,
        tickettype_id: ticket.tickettype_id,
        company_id: ticket.company_id
      })
        .then(response => {
          this.fetchTickets(); // Refresh the tickets list
        })
        .catch(error => {
          console.error('Error creating reminder:', error);
        }).finally(() => {
          this.$updateLoading(-1);
        });
    },
    copyTicketUrl(ticketId) {
      const ticketUrl = `https://atms.aimit.io/ticket/${ticketId}`;
      navigator.clipboard.writeText(ticketUrl)
        .then(() => {
          //
          this.copiedTicketId = ticketId;
          setTimeout(() => {
            this.copiedTicketId = null; // Remove notification after 2 seconds
          }, 2000);
        })
        .catch(err => {
          console.error("Error copying ticket URL:", err);
        });
    },
    handleTicketTransfer({ ticketId, curTechId, newTechId, sendEmail, transferNotes }) {
      this.$updateLoading(1);
      const ticket = this.tickets.find(t => t.id === ticketId);
      if (!ticket) {
        throw new Error(`Ticket with id ${ticketId} not found`);
      }
      axios.post('ticket/transfer', {
        ticketId: ticketId,
        curTechId: curTechId,
        newTechId: newTechId,
        sendEmail: sendEmail,
        transferNotes: transferNotes
      })
        .then(response => {
          this.fetchTickets(); // Refresh the tickets list
        })
        .catch(error => {
          console.error('Error transferring ticket:', error);
        }).finally(() => {
          this.$updateLoading(-1);
        });
    },
    handleCloseTicket({ ticketId, techId, day, sendEmail, closingNote, ticketTypeId, aiEdited, notifyCompany }) {
      this.$updateLoading(1);
      const ticket = this.tickets.find(t => t.id === ticketId);
      if (!ticket) {
        throw new Error(`Ticket with id ${ticketId} not found`);
      }
      axios.post('ticket/close', {
        ticketId: ticketId,
        techId: techId,
        day: day,
        sendEmail: sendEmail,
        closingNote: closingNote,
        ticketTypeId: ticketTypeId,
        aiEdited: aiEdited,
        notifyCompany: notifyCompany
      })
        .then(response => {
          this.fetchTickets();
        })
        .catch(error => {
          console.error('Error closing ticket:', error);
        }).finally(() => {
          this.$updateLoading(-1);
        });
    },
    async handleReopenTicket({ ticketId, day, note }) {
      this.$updateLoading(1);
      try {
        await axios.post('ticket/reopen', {
          ticketId,
          day,
          note
        });
        this.toast.success('Ticket reopened successfully!');
        this.fetchTickets();
      } catch (error) {
        console.error('Error reopening ticket:', error);
        this.toast.error(error?.response?.data?.message || 'Failed to reopen ticket');
      } finally {
        this.$updateLoading(-1);
        this.reopenTicketModal = false;
      }
    },
    sortTickets(column) {
      if (this.sortKey === column) {
        // Toggle sort order if the same column is clicked
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        // Set the new sort key and default to ascending order
        this.sortKey = column;
        this.sortOrder = 'asc';
      }
      this.fetchTickets();
    },
    getSortClass(column) {
      if (this.sortKey === column) {
        return this.sortOrder === 'asc' ? 'sort-asc' : 'sort-desc';
      }
      return '';
    },
    // getUrgencyClass(urgencycolor) {
    //   console.log(urgencycolor);
    //   return [
    //     'inline-flex',
    //     'items-center',
    //     'rounded-md',
    //     'px-2',
    //     'py-1',
    //     'text-xs',
    //     'font-medium',
    //     'text-' + urgencycolor + '-700',
    //     'bg-' + urgencycolor + '-50',
    //     'ring-' + urgencycolor + '-700/10',
    //     'ring-1',
    //     'ring-inset',
    //     'dark:bg-' + urgencycolor + '-50/20',
    //     'dark:ring-' + urgencycolor + '-400/75',
    //     'dark:text-' + urgencycolor + '-300'
    //   ];
    // },
    toggleFilters() {
      this.showFilters = !this.showFilters;
    },
    applyFilters() {
      let hasError = false;
      // Validate Client
      if (
        this.companyName !== 'All' &&
        this.companyName !== '' && (this.filters.client_id == '' ||
          !this.clients.find(c => c.companyname.toLowerCase() === this.companyName.toLowerCase()))
      ) {
        this.invalidInputs.client = true;
        hasError = true;
      } else {
        this.invalidInputs.client = false;
      }

      // Validate Requester
      if (
        this.filters.requester !== 'All' &&
        this.filters.requester !== '' && (this.filters.requester_id == '' ||
          !this.requesters.find(r => r.name.toLowerCase() === this.filters.requester.toLowerCase()))
      ) {
        this.invalidInputs.requester = true;
        hasError = true;
      } else {
        this.invalidInputs.requester = false;
      }

      // Validate Type
      if (
        this.filters.type !== 'All' &&
        this.filters.type !== '' && (this.filters.type_id == '' ||
          !this.ticketTypes.find(t => t.description.toLowerCase() === this.filters.type.toLowerCase()))
      ) {
        this.invalidInputs.type = true;
        hasError = true;
      } else {
        this.invalidInputs.type = false;
      }

      if (hasError) return;
      this.page = 1;
      this.fetchTickets();
      this.showFilters = false;
    },
    clearFilters() {
      this.filters = {
        status: 'Open',
        date_type: '',
        client_id: 'All',
        requester_id: 'All',
        requester: 'All',
        type: 'All',
        type_id: 'All',
        category_id: 'All',
        sprint_id: 'All',
        aging: 'All',
        billingType: 'All',
        tech_id: 'All',
        department: 'All',
        startDate: '',
        endDate: '',
        keywords: '',
        tab: this.filters.tab,
        pastdueonly: false
      };
      if (!this.userroles.includes(80)) {
        this.filters.tech_id = localStorage.getItem('user_id');
        this.filters.department = this.userDepartmentId;
      }
      localStorage.removeItem('ticketFilters');
      this.companyName = 'All';
      this.fetchTickets();
    },
    setTodayFilters() {
      this.filters = {
        status: 'Open',
        date_type: 'promised',
        client_id: 'All',
        requester_id: 'All',
        requester: 'All',
        type: 'All',
        type_id: 'All',
        category_id: 'All',
        sprint_id: 'All',
        aging: 'All',
        billingType: 'All',
        tech_id: localStorage.getItem('user_id'),
        department: 'All',
        startDate: '',
        endDate: new Date().toLocaleDateString('en-CA'),
        keywords: '',
        tab: this.filters.tab,
        pastdueonly: false
      };
      this.companyName = 'All';
      this.userDepartmentId = 'All';
      this.fetchTickets();
    },
    setClientFilter(clientId) {
      const parsedId = clientId === 'All' ? 'All' : Number(clientId);
      const client = this.clientsWithOpenTickets.find(c => c.id === parsedId);

      this.filters = {
        status: 'Open',
        date_type: '',
        client_id: clientId,
        requester_id: 'All',
        requester: 'All',
        type: 'All',
        type_id: 'All',
        category_id: 'All',
        sprint_id: 'All',
        aging: 'All',
        billingType: 'All',
        tech_id: this.filters.tech_id,
        department: 'All',
        startDate: '',
        endDate: '',
        keywords: '',
        tab: this.filters.tab,
        pastdueonly: false,
      };

      this.userDepartmentId = 'All';
      this.companyName = client ? client.companyname : 'All';
      this.fetchTickets();
    },
    resetFilters() {
      this.filters = {
        status: 'Open',
        date_type: '',
        client_id: 'All',
        requester_id: 'All',
        requester: 'All',
        type: 'All',
        type_id: 'All',
        category_id: 'All',
        sprint_id: 'All',
        aging: 'All',
        billingType: 'All',
        tech_id: 'All',
        department: 'All',
        startDate: '',
        endDate: '',
        keywords: '',
        tab: this.filters.tab,
        pastdueonly: false // New filter for past due only
      };
      if (!this.userroles.includes(80)) {
        this.filters.tech_id = localStorage.getItem('user_id');
        this.filters.department = this.userDepartmentId;
      }
      this.companyName = 'All'
      // this.fetchTickets();
    },
    isNearBottomRow(ticket) {
      const el = document.getElementById(`priority-dropdown-${ticket.id}`);
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      return rect.bottom + 250 > window.innerHeight;
    },
    setTab(tabName) {
      this.filters.tab = tabName;
      this.page = 1;
      this.fetchTickets();
    },
    debounce(func, wait, immediate) {
      let timeout;
      return function () {
        const context = this, args = arguments;
        const later = function () {
          timeout = null;
          if (!immediate) func.apply(context, args);
        };
        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
      };
    },
    initQuickNoteEditor(ticketId) {
      this.$nextTick(() => {
        let el = this.$refs[`quicknote_editor_${ticketId}`];
        if (Array.isArray(el)) el = el[0];
        if (!el) return;

        try {
          const ed = new Editor({
            el,
            initialValue: this.quickNoteText || '',
            height: '300px',
            previewStyle: 'vertical',
            initialEditType: 'markdown',
            usageStatistics: false,
            theme: this.isdark ? 'dark' : 'default',
            toolbarItems: [
              ['heading', 'bold', 'italic', 'strike'],
              ['hr', 'quote'],
              ['ul', 'ol', 'task', 'indent', 'outdent'],
              ['table', 'link'],
              ['code', 'codeblock'],
              ['scrollSync'],
            ],
            hooks: {
              addImageBlobHook: () => false
            }
          });
          this.quickNoteEditors[ticketId] = ed;
        } catch (e) {
          console.error('QuickNote editor init failed for', ticketId, e);
        }
      });
    },

    destroyQuickNoteEditor(ticketId) {
      const ed = this.quickNoteEditors[ticketId];
      if (ed) {
        try { ed.destroy(); } catch (_) { }
        delete this.quickNoteEditors[ticketId];
      }
    },
    isNoteEditing(noteId) {
      return !!this.editingNotes[noteId];
    },

    startEditNote(ticket, note) {
      const viewerId = `note-viewer-${ticket.id}-${note.id}`;
      this.destroyViewer(viewerId);
      if (this.editingNotes[note.id]?.editor) {
        try { this.editingNotes[note.id].editor.destroy(); } catch (_) { }
      }

      this.editingNotes = {
        ...this.editingNotes,
        [note.id]: {
          ticketId: ticket.id,
          description: this.fixMarkdownIndentation(note.description || ''),
          time: parseFloat(note.totaltime || 0).toFixed(2),
          ia: parseFloat(note.ignoranceadjustment || 0).toFixed(2),
          publish: !!note.publish_to_portal,
          editor: null
        }
      };

      this.$nextTick(() => this.initNoteEditor(note.id));
    },

    initNoteEditor(noteId) {
      let el = this.$refs['note_editor_' + noteId];
      if (Array.isArray(el)) el = el[0];
      if (!el) {
        console.error('Note editor container not found for', noteId);
        return;
      }
      const st = this.editingNotes[noteId];
      if (!st) return;

      const editor = new Editor({
        el,
        initialValue: st.description,
        height: '500px',
        previewStyle: 'vertical',
        initialEditType: 'markdown',
        usageStatistics: false,
        theme: this.isdark ? 'dark' : 'default',
        toolbarItems: [
          ['heading', 'bold', 'italic', 'strike'],
          ['hr', 'quote'],
          ['ul', 'ol', 'task', 'indent', 'outdent'],
          ['table', 'link'],
          ['code', 'codeblock'],
          ['scrollSync'],
        ],
        hooks: {
          addImageBlobHook: () => false
        }
      });

      this.editingNotes[noteId].editor = editor;
    },
    adjustTime(noteId, field, delta) {
      let val = parseFloat(this.editingNotes[noteId][field]) || 0;
      val += delta;
      this.editingNotes[noteId][field] = Math.max(0, Math.round(val * 100) / 100);
    },
    async saveEditNote(ticket, note) {
      const st = this.editingNotes[note.id];
      if (!st || !st.editor) return;

      try {
        this.$updateLoading(1);

        const payload = {
          description: st.editor.getMarkdown(),
          ia: parseFloat(st.ia || 0),
          total: parseFloat(st.time || 0),
          publish: st.publish ? 1 : 0,
          client_id: ticket.company_id || 0,
          ticket_id: ticket.id || 0,
        };

        await axios.post(`/job/notes/${note.id}`, payload);

        await this.fetchTickets();

        try { st.editor.destroy(); } catch (_) { }
        delete this.editingNotes[note.id];
        this.editingNotes = { ...this.editingNotes };
        this.$nextTick(() => this.initViewersForTicket(ticket.id));

      } catch (err) {
        console.error('Error saving note:', err);
        this.toast?.error?.('Failed to save note');
      } finally {
        this.$updateLoading(-1);
      }
    },

    cancelEditNote(noteId) {
      const st = this.editingNotes[noteId];
      if (st?.editor) {
        try { st.editor.destroy(); } catch (_) { }
      }
      delete this.editingNotes[noteId];
      this.editingNotes = { ...this.editingNotes }
      const ticketId = st?.ticketId;
      if (ticketId) {
        this.$nextTick(() => this.initViewersForTicket(ticketId));
      }
    },

    async exportTicketsToCSV() {
      this.$updateLoading(1);
      try {

        const response = await axios.post('ticket/export', {
          ...this.filters,
          sort_column: this.sortKey,
          sort_order: this.sortOrder.toUpperCase()
        });
        const exportTickets = response.data.data;

        const headers = ["ID", "Ticket Type", "Details", "Priority", "Requested By", "Client", "Assigned To", "Last Activity", "Logged Time", "Days Old", "Status"];
        let csvContent = headers.join(",") + "\r\n";

        exportTickets.forEach(ticket => {
          const status = ticket.completed == "0000-00-00 00:00:00" ? "Open" : "Closed";
          const row = [
            ticket.id,
            `"${ticket.ticket_type?.description || ''}"`,
            `"${(ticket.description || ticket.desc_short || '').replace(/"/g, '""')}"`,
            `"${ticket.pdate} ${ticket.ptime}"`,
            `"${ticket.requesterName || ''}"`,
            `"${ticket.clientName || ''}"`,
            `"${ticket.assigned_to || ''}"`,
            `"${ticket.lastjobdate || 'N/A'}"`,
            `"${ticket.logged_time || 'N/A'}"`,
            `"${ticket.daysold || 'N/A'}"`,
            `"${status}"`
          ];
          csvContent += row.join(",") + "\r\n";
        });

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "tickets.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error exporting tickets:", error);
      } finally {
        this.$updateLoading(-1);
      }
    },
    handlePageChange(newPage) {
      if (newPage !== '...' && newPage > 0 && newPage <= this.totalPages) {
        this.page = newPage;
        this.fetchTickets(); // Fetch tickets based on the new page
      }
    },
    openMergeTicketModal(ticketId) {
      this.selectedTicketId = ticketId;
      this.mergeTicketModal = true;
    },
    async openCreateReminderModal(ticketId) {
      const ticket = this.tickets.find(t => t.id === ticketId);
      if (!ticket) {
        console.error(`Ticket with id ${ticketId} not found`);
        return; // Exit the function if no ticket is found
      }
      this.selectedTicketId = ticketId;
      this.selectedTech = ticket.tech_id;
      this.selectedTicketTypeId = ticket.ticket_type.id;

      try {
        this.$updateLoading(1);
        const response = await axios.get(`${axios.defaults.baseURL}user/active/transfer/${ticket.company_id}`);
        this.clientTechs = response.data;

        this.createReminderModal = true;
      } catch (error) {
        console.error("Error fetching active users for the client:", error);
      } finally {
        this.$updateLoading(-1);
      }
    },
    openReopenTicketModal(ticket) {
      this.selectedTicketId = ticket.id;
      this.selectedTicketLastActivity = new Date();
      this.reopenTicketModal = true;
    },
    async openSetCompleteTicketModal(ticket) {
      // const ticket = this.tickets.find(t => t.id === ticketId);
      // if (!ticket) {
      //   console.error(`Ticket with id ${ticketId} not found`);
      //   return; // Exit the function if no ticket is found
      // }
      this.selectedTicketLastActivity = ticket.last_activity;
      this.selectedTicketId = ticket.id;
      this.selectedTicketTypeId = ticket.ticket_type.id;
      // Unassigned tickets (tech_id 0) have no user_profile; fall back to the raw
      // tech_id so closing an unassigned ticket doesn't throw (seen from the external
      // tech view, which works with unassigned tickets).
      this.selectedTech = ticket.user_profile?.id ?? ticket.tech_id;
      this.selectedTicketNotification = ticket.emailnotification;
      this.selectedRequesterName = this.getRequesterName(ticket);
      this.selectedTicket = ticket;
      this.selectedRequesterEmail = this.getRequesterEmail(ticket);
      this.selectedRequesterPhone = this.getRequesterPhone(ticket).number || '';
      try {
        this.$updateLoading(1);
        const response = await axios.get(`${axios.defaults.baseURL}user/active/transfer/${ticket.company_id}`);
        this.clientTechs = response.data;

        this.setCompleteTicketModal = true;
      } catch (error) {
        console.error("Error fetching active users for the client:", error);
      } finally {
        this.$updateLoading(-1);
      }
    },
    async loadDefaultView() {
      try {
        const response = await axios.get(`ticket/get-default-view/${this.userId}`);

        if (response.data && response.data.view) {
          const view = response.data.view;

          // Apply the view settings to the current filters
          this.filters.status = view.status;
          this.filters.tab = view.tab;
          this.filters.date_type = view.date_type;
          this.filters.client_id = view.client_id !== null ? view.client_id : 'All';
          this.filters.requester_id = view.requester_id !== null ? view.requester_id : 'All';
          this.filters.requester = view.requester || 'All';
          this.filters.type = view.type || 'All';
          this.filters.type_id = view.type_id !== null ? view.type_id : 'All';
          this.filters.category_id = view.category_id !== null ? view.category_id : 'All';
          this.filters.aging = view.aging || 'All';
          this.filters.billingType = view.billing_type || 'All';

          // For numeric ID fields, ensure proper type conversion
          this.filters.tech_id = view.tech_id !== null ? parseInt(view.tech_id, 10) || view.tech_id : 'All';
          this.filters.department = view.department !== null ? parseInt(view.department, 10) || view.department : 'All';

          this.filters.startDate = view.start_date || '';
          this.filters.endDate = view.end_date || '';
          this.filters.keywords = view.keywords || '';
          this.filters.pastdueonly = Boolean(view.pastdue_only);
          this.sortKey = view.sort_key || 'priority';
          this.sortOrder = view.sort_order || 'asc';

          // Update companyName to match the selected client_id
          if (view.client_id && view.client_id !== 'All') {
            const client = this.clients.find(c => c.id.toString() === view.client_id.toString());
            if (client) {
              this.companyName = client.companyname;
            } else {
              this.companyName = 'All';
            }
          } else {
            this.companyName = 'All';
          }
          if (!this.userroles.includes(80) && this.filters.tech_id === 'All') {
            this.filters.tech_id = localStorage.getItem('user_id');
            this.filters.department = this.userDepartmentId;
          }
          // Fetch tickets with these filters
          this.fetchTickets();
        } else {
          if (!this.userroles.includes(80) && this.filters.tech_id === 'All') {
            this.filters.tech_id = localStorage.getItem('user_id');
            this.filters.department = this.userDepartmentId;
          }
          // If no saved view, just fetch with default filters
          this.fetchTickets();
        }
      } catch (error) {
        console.error('Error loading default view:', error);
        if (!this.userroles.includes(80) && this.filters.tech_id === 'All') {
          this.filters.tech_id = localStorage.getItem('user_id');
          this.filters.department = this.userDepartmentId;
        }
        // Fall back to default filters if there's an error
        this.fetchTickets();
      }
    },
    async saveView() {
      try {
        this.$updateLoading(1);
        const viewData = {
          user_id: this.userId,
          view_name: `Default View (${new Date().toLocaleDateString()})`,
          is_default: true,
          status: this.filters.status,
          date_type: this.filters.date_type,
          client_id: this.filters.client_id,
          requester_id: this.filters.requester_id,
          requester: this.filters.requester,
          type: this.filters.type,
          tab: this.filters.tab,
          type_id: this.filters.type_id,
          category_id: this.filters.category_id,
          aging: this.filters.aging,
          billing_type: this.filters.billingType,
          tech_id: this.filters.tech_id,
          department: this.filters.department,
          start_date: this.filters.startDate,
          end_date: this.filters.endDate,
          keywords: this.filters.keywords,
          pastdue_only: this.filters.pastdueonly,
          sort_key: this.sortKey,
          sort_order: this.sortOrder
        };

        // Fix the URL path
        const response = await axios.post('ticket/save-view', viewData);

        if (response.data.success) {
          alert('View saved successfully');
        } else {
          throw new Error(response.data.message || 'Error saving view');
        }
      } catch (error) {
        console.error('Error saving view:', error);
        alert('Failed to save view: ' + (error.response?.data?.message || error.message));
      } finally {
        this.$updateLoading(-1);
      }
    },
    async openTransferTicketModal(ticketId) {
      const ticket = this.tickets.find(t => t.id === ticketId);
      if (!ticket) {
        console.error(`Ticket with id ${ticketId} not found`);
        return; // Exit the function if no ticket is found
      }
      this.selectedTicketId = ticketId;
      this.selectedTech = ticket.tech_id;
      try {
        this.$updateLoading(1);
        // Await the API call to fetch active users (client techs)
        const response = await axios.get(`${axios.defaults.baseURL}user/active/transfer/${ticket.company_id}`);
        this.clientTechs = response.data; // Store the client techs in a variable

        // Open the modal only after fetching the techs
        this.transferTicketModal = true;
      } catch (error) {
        console.error("Error fetching active users for the client:", error);
      } finally {
        this.$updateLoading(-1);
      }
      // this.transferTicketModal = true;
    },
    destroyViewer(viewerId) {
      const v = this.viewers[viewerId];
      if (v) {
        try { v.destroy(); } catch (_) { }
        delete this.viewers[viewerId];
      }
    },
    initViewer(viewerId, content) {
      this.destroyViewer(viewerId);

      this.$nextTick(() => {
        const el = document.getElementById(viewerId);
        if (!el) {
          return;
        }
        try {
          this.viewers[viewerId] = new Viewer({
            el,
            initialValue: this.fixMarkdownIndentation(content || ''),
            theme: this.isdark ? 'dark' : 'default',
          });
        } catch (e) {
          console.error('Viewer init failed for', viewerId, e);
        }
      });
    },
    fixMarkdownIndentation(text) {
      return text
        .split('\n')
        .map(line => {
          // Fix indentation for list items that start with ' - '
          if (line.startsWith(' - ')) {
            line = '    - ' + line.slice(3);
          }

          // Fix markdown headings without space, e.g., '###Heading' → '### Heading'
          line = line.replace(/^(#{1,6})([^\s#])/g, '$1 $2');

          return line;
        })
        .join('\n');
    },
    toggleDetails(ticketId) {
      this.expandedTicketId = this.expandedTicketId === ticketId ? null : ticketId;
    },
    isDetailsVisible(ticketId) {
      return this.expandedTicketId === ticketId;
    },
    startJob(ticketId) {
      // window.open(`https://atms.aimitservices.com/jobList.php?func=quick_add_job_from_ticket&ticket_id=${ticketId}`, '_blank');
      this.$updateLoading(1);
      const ticket = this.tickets.find(t => t.id === ticketId);
      if (ticket.acknowledged == 0 && ticket.tech_id == this.userId) {
        this.acknowledgeTicket(ticketId);
      }
      axios.post('job/quickjob', { ticketId })
        .then(response => {
          // window.open('/jobs', '_blank');
          this.toast.success(response.data.message);
          this.fetchTickets();
        })
        .catch(error => {
          console.error("Failed to create quick job:", error);
        })
        .finally(() => {
          this.$updateLoading(-1);
        });
    },
    addJob(ticketId) {
      const ticket = this.tickets.find(t => t.id === ticketId);
      if (!ticket) {
        console.error('Ticket not found:', ticketId);
        return;
      }
      if (ticket.acknowledged == 0 && ticket.tech_id == this.userId) {
        this.acknowledgeTicket(ticketId);
      }
      this.$router.push({ name: 'addEditJob', query: { ticketId: ticketId } });
      // window.open(`https://atms.aimitservices.com/addEdit.php?ticketID=${ticketId}&return_to=tickets-summary-IMPROVED.php`, '_blank');
    },
    openTicketInvoice(ticketId) {
      const ticket = this.tickets.find(t => t.id === ticketId);
      if (!ticket) {
        console.error('Ticket not found:', ticketId);
        return;
      }
      let clientId = '';
      if (ticket.company_id != 0) {
        clientId = ticket.company_id;
      }
      window.open(`https://atms.aimitservices.com/administration.php?doSearch=true&jobStatus=all&companyID=${clientId}&search=${ticketId}&summarySelection=none`, '_blank');
    },
    toggleQuickNoteForm(ticketId) {
      const willShow = !this.quickNoteFormVisible[ticketId];
      if (willShow) {
        this.quickNoteText = '';
        this.quickNoteTime = 0;
        this.quickNoteIA = 0;
        this.quickNotePublish = false;
      } else {
        this.destroyQuickNoteEditor(ticketId);
      }
      this.quickNoteFormVisible = {
        ...this.quickNoteFormVisible,
        [ticketId]: willShow
      };

      if (willShow) {
        this.destroyQuickNoteEditor(ticketId);
        this.$nextTick(() => this.initQuickNoteEditor(ticketId));
      }
    },
    isQuickNoteFormVisible(ticketId) {
      return this.quickNoteFormVisible[ticketId] || false;
    },
    editTicket(id) {
      this.$router.push({ name: 'editTicket', params: { id: id } });
    },
    openTicket(id) {
      this.$router.push({ name: 'ticketDetails', params: { ticketId: id } });
    },
    acknowledgeTicket(id) {
      this.$updateLoading(1);
      const ticket = this.tickets.find(t => t.id === id);
      if (!ticket) {
        throw new Error(`Ticket with id ${id} not found`);
      }
      axios.post('ticket/acknowledge', {
        ticketId: id,
        ack: 1
      })
        .then(response => {
          this.fetchTickets(); // Refresh the tickets list
        })
        .catch(error => {
          console.error('Error acknowledging ticket:', error);
        }).finally(() => {
          this.$updateLoading(-1);
        });
    },
    async deleteTicket(ticketId) {
      const ticket = this.tickets.find(t => t.id === ticketId);
      if (!ticket) {
        alert("Ticket not found.");
        return;
      }

      // Prevent deletion if worked time exists
      if (ticket.logged_time > 0) {
        alert(`Cannot delete. This ticket has ${ticket.logged_time} hours of worked time.`);
        return;
      }

      const tmp = confirm(`Are you sure you would like to delete ticket #${ticket.id}?`);
      if (!tmp) {
        return;
      }

      this.$updateLoading(1);
      try {
        const response = await axios.post('ticket/summary-delete', { delete: ticketId });
        this.fetchTickets(); // Refresh the tickets list
      } catch (error) {
        console.error('Error deleting ticket:', error);
      } finally {
        this.$updateLoading(-1);
      }
    },
    closeClientDropdown() {
      this.dropdownState.client.visible = false;
    },
    closeReqDropdown() {
      this.dropdownState.requester.visible = false;
    },
    closeTypeDropdown() {
      this.dropdownState.type.visible = false;
    },
    focusClient() {
      if (this.companyName == 'All') {
        this.companyName = '';
        this.filters.client_id = '';
      }
      // this.showClientDropdown = true;
      this.dropdownState.client.visible = true
    },
    clearClient() {
      this.companyName = 'All';
      this.filters.client_id = 'All';
      this.invalidInputs.client = false;
    },
    clearReq() {
      this.filters.requester_id = 'All';
      this.filters.requester = 'All';
      this.invalidInputs.requester = false;
    },
    clearReqType() {
      this.filters.type = 'All';
      this.filters.type_id = 'All';
      this.invalidInputs.type = false;
    },
    focusReq() {
      if (this.filters.requester == 'All') {
        this.filters.requester = '';
        this.filters.requester_id = '';
      }
      // this.showReqDropdown = true;
      this.dropdownState.requester.visible = true
    },
    focusReqType() {
      if (this.filters.type == 'All') {
        this.filters.type = '';
        this.filters.type_id = '';
      }
      // this.showReqTypeDropdown = true;
      this.dropdownState.type.visible = true
    },
    emptyReq() {
      setTimeout(() => {
        if (this.filters.requester === '') {
          this.filters.requester = 'All';
          this.filters.requester_id = '';
        }
      }, 500);
    },
    emptyClient() {
      setTimeout(() => {
        if (this.companyName === '') {
          this.companyName = 'All';
          this.filters.client_id = '';
        }
      }, 500);
    },
    emptyType() {
      setTimeout(() => {
        if (this.filters.type === '') {
          this.filters.type = 'All';
          this.filters.type_id = '';
        }
      }, 500);
    },
    setClient(client) {
      this.filters = {
        status: 'Open',
        date_type: '',
        client_id: client.id,
        requester_id: 'All',
        requester: 'All',
        type: 'All',
        type_id: 'All',
        category_id: 'All',
        sprint_id: 'All',
        aging: 'All',
        billingType: 'All',
        tech_id: this.filters.tech_id,
        department: 'All',
        startDate: '',
        endDate: '',
        keywords: '',
        tab: this.filters.tab,
        pastdueonly: false
      };
      this.userDepartmentId = 'All';
      this.companyName = client.companyname;
      this.dropdownState.client.visible = false;
      this.invalidInputs.client = false;
    },
    setReq(req) {
      this.filters.requester = req.name;
      this.filters.requester_id = req.id;
      this.invalidInputs.requester = false;
      this.dropdownState.requester.visible = false;
    },
    setReqType(type) {
      this.filters.type_id = type.id;
      this.filters.type = type.description;
      this.dropdownState.type.visible = false;
      this.invalidInputs.type = false;
    }
  },
  mounted() {
    // this.init()
    // this.fetchTickets();
    this.init().then(() => {
      const shouldLoadDefaultView = localStorage.getItem('load_default_view') === 'true';
      if (shouldLoadDefaultView) {
        // Clear the flag immediately to prevent future auto-loads
        localStorage.setItem('load_default_view', 'false');

        // Load the default view instead of standard fetch
        this.loadDefaultView();
      } else {
        // Otherwise, just fetch tickets with current filters
        this.fetchTickets();
      }
    });
    window.addEventListener('pointermove', this.onPointerMove, { passive: true });
  }
};
</script>

<style scoped>
.dark-mode .scrollable-requester-list {
  background-color: #2d3748;
  color: #a0aec0;
}

.scrollable-requester-list {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #ccc;
}

.details-transition-enter-active {
  transition: all 0.3s ease-in-out;
  overflow: hidden;
}

.details-transition-enter-from {
  opacity: 0;
  transform: translateY(-10px) scale(0.97);
  max-height: 0;
}

table td {
  vertical-align: top !important;
  padding-top: 20px;
}

.details-transition-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
  max-height: 1000px;
}

.quicknote-transition-enter-active,
.quicknote-transition-leave-active {
  transition: all 0.1s ease;
}

.quicknote-transition-enter,
.quicknote-transition-leave-to

/* .quicknote-transition-leave-active in <2.1.8 */
  {
  opacity: 0;
  transform: translateY(-10px);
}

.rotate-180 {
  transform: rotate(180deg);
}

.transition-transform {
  transition: transform 0.1s ease;
}

.sort-asc::after {
  content: '▲';
  font-size: 0.75rem;
  margin-left: 0.5rem;
}

.sort-desc::after {
  content: '▼';
  font-size: 0.75rem;
  margin-left: 0.5rem;
}

.bottom-full {
  bottom: 100%;
  top: auto !important;
}

:deep(.markdown-body a) {
  color: #4485d4;
  text-decoration: underline;
}

.dark :deep(.markdown-body a) {
  color: #60a5fa;
}
</style>
